/**
 * @skaile/asset-manager — programmatic AI asset management API.
 *
 * Manages project-local repositories, dependency resolution, deployment,
 * lock files, patches, and contribution workflows.
 *
 * Usage:
 *   import { AssetManager } from "@skaile/workspaces/asset-manager"
 *   const am = new AssetManager({ projectDir: "/path/to/project" })
 *   await am.install()
 */

import { spawnSync } from "node:child_process";
import {
  existsSync,
  lstatSync,
  mkdirSync,
  readdirSync,
  readFileSync,
  rmSync,
  unlinkSync,
  writeFileSync,
} from "node:fs";
import { dirname, join, resolve, sep } from "node:path";
import type {
  AiComponent,
  AssetRef,
  CatalogEntry,
  DriverTarget,
  LockFile,
  OverrideEntry,
  ProvenanceCandidate,
  SkWorkspaceConfig,
  SourceClone,
  SourceDeclaration,
  SourceEntry,
  StoreEntry,
  StoreFetcher,
} from "@skaile/workspaces/core";
import {
  assetRefToStr,
  buildLockFile,
  buildProvenanceIndex,
  checkRepoStatus,
  coerceVersion,
  applyPatch as coreApplyPatch,
  deployedBase,
  deployedDir,
  ensureRepo,
  extractForPatch,
  factoryAssetRoots,
  generatePatch,
  getGlobalCacheDir,
  getRepoCommit,
  globalCacheRoot,
  globalSkaileDir,
  isDeployed,
  loadMcpServerDeclarations,
  matchPin,
  migrateLegacyDir,
  parseAssetRef,
  parseRequires,
  readLinks,
  readLock,
  resolveAll,
  resolveAsset,
  resolveGlobalBackends,
  resolveGlobalSkWorkspaceConfig,
  resolveRuntimeAssets,
  resolveSkWorkspaceConfig,
  savePatch,
  scanDirectory,
  scanRepo,
  SKAILE_YAML_DEFAULT,
  slugifyAssetName,
  sourceCacheKey,
  sourceShortName,
  storeManifestSlug,
  verifyLock,
  writeLock,
} from "@skaile/workspaces/core";
import type { ResolveResult } from "@skaile/workspaces/core";
import type { AssetDefinition, AssetFetcher } from "@skaile/workspaces/library";
import { getStoreManifestsDir, resolveLibraryDir } from "@skaile/workspaces/library";
import type { InstallManifest } from "@skaile/workspaces/types";
import { gt as semverGt } from "semver";
import { parse as parseYaml, stringify as stringifyYaml } from "yaml";
import { commitChanges } from "./contrib.js";
import type { HistoryEntry } from "./history.js";
import { appendHistory, clearHistory, getRecentHistory } from "./history.js";
import type { DeployOptions } from "./installer.js";
import { createScaffold, deployAll, removeAsset } from "./installer.js";
import { renderMcpServerConfig } from "./mcp-config.js";

/**
 * Normalize a git remote URL to a comparable identity so the same repo declared
 * in different syntaxes (`https://…`, `git@host:…`, trailing `/`, `.git` suffix,
 * case) compares equal. Used to dedupe the implicit factory source against an
 * explicit user declaration.
 */
export function normalizeRepoUrl(url: string): string {
  return url
    .trim()
    .replace(/^git@([^:]+):/, "https://$1/") // git@host:org/repo → https://host/org/repo
    .replace(/^[a-z]+:\/\//i, "") // drop scheme
    .replace(/\.git$/i, "")
    .replace(/\/+$/, "")
    .toLowerCase();
}

/**
 * Absolute path to a source URL's curated store manifest
 * (`~/.skaile/store/manifests/<slug>.yaml`, authored by `source manifest init`).
 * Keyed by the collision-free {@link storeManifestSlug}, matching the CLI writer
 * + the `source add` overlay reader, so the provenance walker honors the same
 * curated inventory `search --local` shows.
 */
function storeOverlayPathForUrl(url: string): string {
  return join(getStoreManifestsDir(), `${storeManifestSlug(url)}.yaml`);
}

/**
 * Declared pin per source cache key. Every entry point that hands `sources:`
 * declarations to `ensureRepo` MUST pass these through — an unpinned managed
 * clone is pulled to the branch tip, so a dropped pin silently moves a pinned
 * source (and, from `lock()`, launders the branch-tip SHA into the lockfile).
 */
function pinsBySlug(sources: SourceEntry[]): Map<string, string | undefined> {
  return new Map(sources.map((s) => [sourceCacheKey(s.url), s.pin]));
}

/** Primary manifest filename for each asset kind (the deploy entry point). */
const MD_BY_KIND: Record<string, string> = {
  skill: "SKILL.md",
  agent: "AGENT.md",
  bundle: "BUNDLE.md",
  "mcp-server": "MCP.md",
  connector: "CONNECTOR.md",
  prompt: "PROMPT.md",
  contract: "CONTRACT.md",
};

/**
 * Pick a candidate's primary manifest file from its `files[]`. Fixed-name kinds
 * (skill/agent/contract/…) match their `MD_BY_KIND` basename; `prompt` and
 * `flow` use their `<name>.prompt.md` / `<name>.flow.{yaml,json}` suffixes
 * (these have no fixed basename, so an exact-name match alone would miss them).
 */
function matchManifestPath(kind: string, paths: readonly string[]): string | undefined {
  const exact = MD_BY_KIND[kind];
  if (exact) {
    const hit = paths.find((p) => p.endsWith(exact));
    if (hit) return hit;
  }
  if (kind === "prompt") return paths.find((p) => p.endsWith(".prompt.md"));
  if (kind === "flow") {
    return paths.find((p) => p.endsWith(".flow.yaml") || p.endsWith(".flow.json"));
  }
  return undefined;
}

/**
 * Asset kinds that are never **copied into a driver target dir** as files —
 * bundles resolve to their members, mcp-servers are wired at session startup,
 * and the mixin kinds are resolved at session creation. Mirrors the skip set in
 * `deployAll`, and says nothing about whether store bytes are fetched:
 * `mcp-server` IS staged into the store cache (see `stageStoreAssets`), because
 * its wiring reads a real `MCP.md` off disk.
 */
export const NON_DEPLOYABLE_KINDS = new Set([
  "bundle",
  "mcp-server",
  "persona",
  "ruleset",
  "knowledge",
]);

/**
 * Drop a publisher scope so a declared `@pub/name` compares equal to an on-disk
 * / declared `name` (both directions are checked at the call sites).
 */
function unscopedName(x: string): string {
  return x.includes("/") ? x.split("/").pop()! : x;
}

/**
 * Longest common leading directory prefix across `paths` (slash-joined, no
 * trailing slash). Mirrors the prefix-strip `installFromManifest` flat mode
 * applies, so a staged asset's manifest can be located deterministically.
 */
function commonDirPrefix(paths: readonly string[]): string {
  if (paths.length === 0) return "";
  const dirs = paths.map((p) => p.split("/").slice(0, -1));
  let common = dirs[0]!;
  for (let i = 1; i < dirs.length && common.length > 0; i++) {
    const segs = dirs[i]!;
    let n = 0;
    while (n < common.length && n < segs.length && common[n] === segs[n]) n++;
    common = common.slice(0, n);
  }
  return common.join("/");
}

/**
 * Index lock entries by their `<kind>:<name>` suffix for deployed-asset lookup.
 * Only entries whose canonical key fully parses (kind, name, publisher, version)
 * are included.
 */
function indexLockByKindName(
  lock: LockFile | null,
): Map<string, { publisher: string; version: string; url: string }> {
  const lockByKindName = new Map<string, { publisher: string; version: string; url: string }>();
  for (const [key] of Object.entries(lock?.assets ?? {})) {
    const parsed = parseCanonicalLockKey(key);
    if (parsed.kind && parsed.name && parsed.publisher && parsed.version) {
      lockByKindName.set(`${parsed.kind}:${parsed.name}`, {
        publisher: parsed.publisher,
        version: parsed.version,
        url: lock!.assets[key]!.source.url,
      });
    }
  }
  return lockByKindName;
}

/** Derive an asset's sync status from its repository's live status. */
function computeSyncStatus(repoStatus: RepoStatusInfo | undefined): string {
  if (!repoStatus) return "unknown";
  if (repoStatus.kind === "local") return "local";
  if (repoStatus.error) return "error";
  if (repoStatus.upToDate) return "synced";
  return "outdated";
}

/**
 * Group overview entries by domain, sort domains alphabetically, and sort
 * entries within each domain by kind then name.
 */
function groupAndSortByDomain(entries: OverviewEntry[]): Map<string, OverviewEntry[]> {
  const byDomain = new Map<string, OverviewEntry[]>();
  for (const e of entries) {
    if (!byDomain.has(e.domain)) byDomain.set(e.domain, []);
    byDomain.get(e.domain)!.push(e);
  }

  const sorted = new Map<string, OverviewEntry[]>();
  for (const key of [...byDomain.keys()].sort()) {
    sorted.set(
      key,
      byDomain
        .get(key)!
        .sort((a, b) =>
          a.kind !== b.kind ? a.kind.localeCompare(b.kind) : a.name.localeCompare(b.name),
        ),
    );
  }
  return sorted;
}

/**
 * Parse a canonical lock key (`<kind>:@<publisher>/<name>#<version>`) into its
 * parts. The lock key is a canonical asset ref, so this delegates to the one ref
 * parser; `AssetRef.pin` is exposed as `version` so the callers' destructuring is
 * unchanged. Stays total — callers rely on `{}`-then-`continue`, but `parseAssetRef`
 * throws, so a genuinely malformed key is caught and returned as `{}`.
 *
 * A **content-sha pseudo-version** key (`#0.0.0-sha.<hash>`, the version every
 * manifest-less source gets) fails `parseAssetRef`'s canonical-pin check. Dropping
 * it to `{}` would strip the identity, so the install reconcile / state machine
 * could never act on such an asset (it silently survives a remove-from-yaml). Fall
 * back to parsing the bare identity, keeping the raw pin as `version`.
 *
 * @internal Exported for unit tests; not part of the stable public surface.
 */
export function parseCanonicalLockKey(key: string): {
  publisher?: string;
  kind?: string;
  name?: string;
  version?: string;
} {
  try {
    const ref = parseAssetRef(key);
    return {
      publisher: ref.publisher,
      kind: ref.kind,
      name: ref.name,
      version: ref.pin,
    };
  } catch {
    // Reuse the shared pin-tolerant parse for the identity; keep the raw pin.
    const loose = parseRefLoose(key);
    if (!loose) return {};
    const hash = key.indexOf("#");
    return {
      publisher: loose.publisher,
      kind: loose.kind,
      name: loose.name,
      version: hash < 0 ? undefined : key.slice(hash + 1),
    };
  }
}

/**
 * Bare asset name of a soft-dep target ref (`@pub/name@ver` → `name`); splits the
 * version off the *last* `@` since the leading one is the scope sigil. The
 * present-check matches on this bare name only (under-reports — the safe direction).
 */
export function softDepName(ref: string): string {
  const at = ref.lastIndexOf("@");
  const base = at > 0 ? ref.slice(0, at) : ref;
  const slash = base.lastIndexOf("/");
  return slash >= 0 ? base.slice(slash + 1) : base;
}

/**
 * Build a **version-less** dep ref (`kind:@publisher/name`) for the `skaile add`
 * suggestion. Omits the version: a manifest-less source resolves under a content-sha
 * pin the index's frontmatter version never matches, so a pinned ref is unaddable.
 */
function depRefFromDef(def: {
  kind: string;
  publisher: string;
  name: string;
  version: string;
}): string | undefined {
  const publisher = def.publisher.replace(/^@/, "");
  if (!publisher) return undefined;
  return assetRefToStr({ kind: def.kind, name: def.name, publisher });
}

/**
 * Parse a ref for identity matching, tolerating a non-canonical pin. A lock ref
 * can pin a content-sha pseudo-version (`#0.0.0-sha.<hash>`) that `parseAssetRef`
 * rejects; the matchers below only need the version-agnostic `kind/publisher/name`,
 * so drop the pin and retry before giving up.
 */
function parseRefLoose(ref: string): ReturnType<typeof parseAssetRef> | null {
  try {
    return parseAssetRef(ref);
  } catch {
    const hash = ref.indexOf("#");
    if (hash < 0) return null;
    try {
      return parseAssetRef(ref.slice(0, hash));
    } catch {
      return null;
    }
  }
}

/**
 * Parse a ref for lookup, tolerating a **missing publisher** (`kind:name`, the
 * shape `listDeployed` emits and the strict `parseAssetRef` rejects). Returns
 * `null` when neither kind nor name is recoverable. Unlike {@link parseRefLoose}
 * (publisher-required, only strips a `#pin`), this keeps the publisher optional.
 *
 * @internal Exported for unit tests; not part of the stable public surface.
 */
export function parseRefTolerant(ref: string): AssetRef | null {
  try {
    return parseAssetRef(ref);
  } catch {
    const colon = ref.indexOf(":");
    if (colon < 0) return null;
    let kind = ref.slice(0, colon).trim();
    // Mirror parseAssetRef's shorthand so a bare `mcp:name` matches `mcp-server` entries.
    if (kind === "mcp") kind = "mcp-server";
    const hash = ref.indexOf("#", colon);
    const name = ref.slice(colon + 1, hash < 0 ? undefined : hash).trim();
    if (!kind || !name) return null;
    return { kind, name, publisher: undefined };
  }
}

/**
 * Find the cached AssetDefinition(s) for an input ref. Tries an exact id match
 * first (canonical refs), then falls back to a name (+publisher/kind when the
 * ref carries them) match so `kind:name` / `kind:@pub/name` inputs resolve. The
 * fallback is version-agnostic, so a lock ref pinned at a content-sha
 * pseudo-version still matches the index def stored under a canonical semver.
 */
function matchDefsForRef<T extends { id: string; kind: string; name: string; publisher: string }>(
  defs: T[],
  ref: string,
): T[] {
  const exact = defs.filter((d) => d.id === ref);
  if (exact.length > 0) return exact;
  const parsed = parseRefLoose(ref);
  if (!parsed) return [];
  const wantPub = parsed.publisher?.replace(/^@/, "");
  return defs.filter(
    (d) =>
      d.name === parsed.name &&
      d.kind === parsed.kind &&
      (wantPub === undefined || d.publisher.replace(/^@/, "") === wantPub),
  );
}

/**
 * Reconciliation state of a declared dependency relative to the lock + disk.
 * `invalid` flags a `dependencies:` entry that isn't a parseable canonical ref —
 * surfaced (not silently dropped) so a `skaile.yaml` typo is visible.
 */
export type DeclaredState = "declared" | "installed" | "drift" | "invalid";

/**
 * A top-level dependency declared in `skaile.yaml`, annotated with its
 * reconciliation {@link DeclaredState}. Transitive (lock-only) deps are never
 * included — {@link AssetManager.listDeclared} walks the declared set only.
 */
export interface DeclaredAsset {
  /** The canonical declared ref exactly as written in `skaile.yaml`. */
  ref: string;
  kind: string;
  name: string;
  state: DeclaredState;
}

/**
 * A global declared dependency from `~/.skaile/skaile.yaml`, reconciled against
 * **one** backend's global deploy dir. The same ref yields one entry per global
 * backend (see {@link AssetManager.globalState}) so an asset can read `installed`
 * for `claude-code` and `drift`/`declared` for `codex` simultaneously.
 */
export interface BackendDeclaredAsset extends DeclaredAsset {
  /** The global backend this state was reconciled against. */
  backend: DriverTarget;
}

/**
 * A body-link soft-dependency edge: asset `from` references target `to` in its
 * body (not its typed `requires`). `toRef` is the parseable `skaile add` ref when
 * the target is in the index, omitted otherwise. Surfaced read-only — never
 * auto-installed (see {@link AssetManager.softDepsOf} / {@link AssetManager.reportSoftDeps}).
 */
export interface SoftDepEdge {
  from: string;
  to: string;
  toRef?: string;
}

/**
 * A soft-dependency target that would be left orphaned by removing some assets:
 * it is still present in the project, came in as `from`'s soft dependency, and
 * nothing else still needs it (neither a soft-dep nor a typed `requires`).
 * Report-only — {@link AssetManager.orphanedSoftDepsAfterRemoving} never removes
 * it; the caller prompts (default No). `targetRef` is the parseable `skaile
 * remove` ref when the target is in the index.
 */
export interface OrphanedSoftDep {
  from: string;
  target: string;
  targetRef?: string;
}

/**
 * Thrown by {@link AssetManager.listDeclared} when the project has no
 * `skaile.yaml`. The CLI maps this to a `skaile init` hint.
 */
export class MissingManifestError extends Error {
  constructor(readonly projectDir: string) {
    super(`No skaile.yaml in ${projectDir}.`);
    this.name = "MissingManifestError";
  }
}

export type { HistoryEntry } from "./history.js";
export {
  appendHistory,
  clearHistory,
  getRecentHistory,
  loadHistory,
} from "./history.js";
// Re-export sub-modules
export type { DeployOptions } from "./installer.js";
export { createScaffold, deployAll, removeAsset } from "./installer.js";
export type {
  RenderMcpConfigOptions,
  RenderMcpConfigResult,
  SkippedMcpServer,
} from "./mcp-config.js";
export { renderMcpServerConfig } from "./mcp-config.js";
export type { Log } from "./renderers.js";
export { renderAgentToFramework } from "./renderers.js";
// Contrib helpers (commitChanges et al.) are still used internally by the
// patch-submit flow but are no longer part of the public API surface — the
// `skaile repo contrib *` command tree was removed on 2026-05-12.

// ── Types ────────────────────────────────────────────────────────────────────

/**
 * Metadata about a repository declared in `skaile.yaml`.
 *
 * @docLink packages/asset-manager/concepts#asset-manager
 */
export interface RepoInfo {
  /** Repository name as declared in `skaile.yaml`. */
  name: string;
  /** How the repo is sourced: cloned from a URL, mounted from a local path, or symlinked via `skaile repo link`. */
  kind: "local" | "remote" | "linked";
  /** Remote URL (present when `kind === "remote"`). */
  url?: string;
  /** Filesystem path (present when `kind === "local"` or `kind === "linked"`). */
  path?: string;
  /** Git branch configured for this repository. */
  branch: string;
  /** Whether the repository has been cloned/resolved locally. */
  cloned: boolean;
}

/**
 * Live sync status for a repository.
 *
 * @docLink packages/asset-manager/concepts#asset-manager
 */
export interface RepoStatusInfo {
  /** Repository name as declared in `skaile.yaml`. */
  name: string;
  /** How the repo is sourced. */
  kind: "local" | "remote" | "linked";
  /** Number of commits the local clone is behind the remote. */
  behind: number;
  /** `true` when the local clone matches the remote HEAD. */
  upToDate: boolean;
  /** Error message when status check failed. */
  error?: string;
}

/**
 * Result of a full `skaile install` or `skaile add` run.
 *
 * @docLink packages/asset-manager/concepts#asset-manager
 */
export interface InstallResult {
  /** Asset refs (`kind:name`) successfully deployed in this run. */
  deployed: string[];
  /** Asset refs (`kind:name`) removed because they are no longer in the resolved set. */
  removed: string[];
  /** Asset refs that could not be resolved in any configured repository. */
  missing: string[];
  /** Whether `skaile.lock.yaml` was written or updated. */
  lockWritten: boolean;
  /**
   * npm packages installed as a side effect of resolving runtime asset peer
   * deps (connectors / mounts declared in skaile.yaml). Empty when nothing
   * needed installing or when the runtime-deps step was skipped.
   */
  npmInstalled?: string[];
  /**
   * npm packages that the runtime-deps step tried but failed to install.
   * Non-fatal — listed for telemetry and CLI reporting.
   */
  npmFailed?: string[];
}

/**
 * An installed asset whose source repository is behind the remote.
 *
 * @docLink packages/asset-manager/concepts#asset-manager
 */
export interface OutdatedEntry {
  /** Asset kind (`skill`, `agent`, `prompt`, `flow`, `contract`). */
  kind: string;
  /** Asset name as registered in the catalog. */
  name: string;
  /** Repository that provides this asset. */
  publisher: string;
  /** Version string recorded in the lock file at install time. */
  currentVersion: string;
  /** Number of commits the local repository is behind the remote. */
  behind: number;
}

/**
 * A node in the resolved dependency tree (returned by `AssetManager.tree()`).
 *
 * @docLink packages/asset-manager/concepts#asset-manager
 */
export interface DependencyNode {
  /** Asset ref (`kind:name`) or `"project"` for the root node. */
  ref: string;
  /** Transitive dependencies resolved by this node. */
  children: DependencyNode[];
}

/**
 * A missing dependency detected by `AssetManager.doctor()`.
 *
 * @docLink packages/asset-manager/concepts#asset-manager
 */
export interface DepIssue {
  /** Kind of the asset that has the unsatisfied requirement. */
  assetKind: string;
  /** Name of the asset that has the unsatisfied requirement. */
  assetName: string;
  /** Kind of the missing dependency. */
  depKind: string;
  /** Name of the missing dependency. */
  depName: string;
  /** `true` when the missing dependency exists in a configured repository but has not been installed. */
  inRepos: boolean;
}

/**
 * A single deployed asset shown in the workspace overview.
 *
 * @docLink packages/asset-manager/concepts#asset-manager
 */
export interface OverviewEntry {
  /** Asset kind (`skill`, `agent`, `prompt`, `flow`, `contract`). */
  kind: string;
  /** Asset name. */
  name: string;
  /** Domain the asset belongs to (from the lock file; `"unknown"` when unresolvable). */
  domain: string;
  /** Repository that provides this asset. */
  publisher: string;
  /** Version string from the lock file. */
  version: string;
  /**
   * Sync status relative to the source repository.
   * - `"synced"` — local clone matches remote HEAD
   * - `"outdated"` — local clone is behind the remote
   * - `"local"` — sourced from a local path, no remote to compare
   * - `"error"` — status check failed
   * - `"unknown"` — repository not found or lock entry missing
   */
  syncStatus: string;
}

/**
 * Aggregated workspace overview returned by `AssetManager.overview()`.
 *
 * @docLink packages/asset-manager/concepts#asset-manager
 */
export interface OverviewResult {
  /** Entries grouped by domain, sorted alphabetically. Each group is sorted by kind then name. */
  byDomain: Map<string, OverviewEntry[]>;
  /** Total number of deployed assets. */
  total: number;
  /** Sync status for each configured repository. */
  repos: RepoStatusInfo[];
}

// ── AssetManager ─────────────────────────────────────────────────────────────

/**
 * Options for constructing an {@link AssetManager}.
 *
 * @docLink packages/asset-manager/concepts#asset-manager
 */
export interface AssetManagerOptions {
  /** Absolute or relative path to the workspace root (must contain `skaile.yaml`). */
  projectDir: string;
  /**
   * Agent framework to target for asset deployment.
   * Determines which framework directories (``.claude/``, ``.omp/``, etc.) are used.
   * Defaults to `"claude-code"`.
   */
  driverTarget?: DriverTarget;
  /**
   * When `true`, the manager operates in **global cross-backend** mode: config
   * SSOT, lock, cache, and history root at `~/.skaile` (not the project), the
   * `dependencies:` manifest edits target `~/.skaile/skaile.yaml`, and deploy
   * fans out to every backend in {@link backends}. When `false` (default),
   * everything is project-scoped against the single {@link driverTarget}.
   */
  global?: boolean;
  /**
   * Global-mode backend set the deploy fans out to. When omitted in global mode,
   * resolved from `~/.skaile/skaile.yaml` `global_backends:` else auto-detection
   * (`resolveGlobalBackends`). Ignored in project mode. A `--backend` filter
   * narrows this to a subset.
   */
  backends?: DriverTarget[];
  /**
   * Catalog read-protocol fetcher used to resolve store-published assets. When
   * omitted, one is built per call from the configured stores
   * (`RemoteCatalogSource`). Injected mainly by tests to stay network-free.
   */
  storeFetcher?: StoreFetcher;
  /**
   * Byte fetcher used to fetch a store asset's files at its pinned commit before
   * deploy. When omitted, the production GitHub fetcher is used. Injected by
   * tests to stay network-free.
   */
  assetFetcher?: AssetFetcher;
  /**
   * Overrides the global `catalog.url` used as a store fallback when the project
   * declares no `stores:`. Pass `""` to disable the fallback entirely. When
   * omitted, the URL is read from `~/.skaile/config.yaml` (`resolveConfig`).
   */
  catalogUrl?: string;
  /**
   * Overrides the index SQLite path read by {@link AssetManager.reportSoftDeps}
   * (default `~/.skaile/index.db` via `resolveIndexPath`). Injected by tests to
   * read a seeded index without touching the user's real one.
   */
  indexPath?: string;
}

/** Canonical git URL identity for the bundled factory source. Never fetched —
 * the bytes live inside the package (`factory-assets/`); the URL is only the
 * match key the provenance walker and `candidateToCatalogEntry` key off. */
export const FACTORY_SOURCE_URL = "https://github.com/skaile-ai/ai-assets";

/**
 * Locate the built-in first-party skills/bundles shipped inside the package.
 *
 * Resolves through the shared {@link factoryAssetRoots} helper — the
 * `SKAILE_FACTORY_ASSETS_DIR` override layer (when set) ahead of the bundled
 * tree (in-repo source during dev, `dist/factory-assets/` after install), so a
 * container's override is honoured here identically to `serve`. Returns the
 * highest-precedence root that carries a `skaile.manifest.yaml` (the publication
 * manifest that names the source's publisher), or `null` when none qualifies —
 * the factory source is then simply not injected rather than erroring.
 */
function resolveFactoryAssetsDir(): string | null {
  for (const dir of factoryAssetRoots()) {
    if (existsSync(join(dir, "skaile.manifest.yaml"))) return dir;
  }
  return null;
}

/** Read the declared `publisher` from a source's root `skaile.manifest.yaml`. */
function readDeclaredPublisher(sourceDir: string): string | undefined {
  try {
    const raw = parseYaml(readFileSync(join(sourceDir, "skaile.manifest.yaml"), "utf8")) as {
      publisher?: unknown;
    } | null;
    return raw && typeof raw.publisher === "string" && raw.publisher.length > 0
      ? raw.publisher
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The bundled factory source `{ dir, publisher }`, or `null` when no usable
 * bundle is present. Resolved once per process — the package location and the
 * bundled `skaile.manifest.yaml` are immutable for the process lifetime, so the
 * `require.resolve` + `existsSync` probes + YAML parse run a single time rather
 * than on every `loadConfig()` call. `undefined` = not yet computed.
 */
let factorySourceCache: { dir: string; publisher: string } | null | undefined;

function resolveFactorySource(): { dir: string; publisher: string } | null {
  if (factorySourceCache !== undefined) return factorySourceCache;
  const dir = resolveFactoryAssetsDir();
  const publisher = dir ? readDeclaredPublisher(dir) : undefined;
  factorySourceCache = dir && publisher ? { dir, publisher } : null;
  return factorySourceCache;
}

/**
 * Programmatic API for managing AI assets in a Skaile workspace.
 *
 * `AssetManager` wraps repository management, catalog search, asset installation,
 * lock-file tracking, patch workflows, and workspace diagnostics. The CLI delegates
 * all `skaile catalog`, `skaile repo`, `skaile install`, `skaile add`, and
 * `skaile init` commands to this class.
 *
 * @example
 * ```typescript
 * import { AssetManager } from "@skaile/workspaces/asset-manager";
 *
 * const am = new AssetManager({ projectDir: "/my-project" });
 *
 * // Install all dependencies declared in skaile.yaml
 * const result = await am.install();
 * console.log(`Deployed: ${result.deployed.join(", ")}`);
 *
 * // Add a single skill (resolves transitive deps, updates skaile.yaml)
 * const deployed = am.add("skill:my-skill");
 *
 * // Search the catalog
 * const skills = am.search("code review", "skill");
 * ```
 *
 * @docLink packages/asset-manager/concepts#asset-manager
 */
export class AssetManager {
  /** Resolved absolute path to the workspace root. */
  readonly projectDir: string;
  /** Agent framework driver target used for all deploy operations. */
  readonly driverTarget: DriverTarget;
  /** Whether this instance operates in global-install mode. */
  readonly global: boolean;
  /** Explicit global-backend override (global mode only); else auto-resolved. */
  private readonly backendsOverride?: DriverTarget[];
  /** Injected catalog fetcher (tests / custom); falls back to a built one. */
  private readonly injectedStoreFetcher?: StoreFetcher;
  /** Injected byte fetcher for store staging; falls back to GitHub. */
  private readonly injectedAssetFetcher?: AssetFetcher;
  /** Injected global catalog URL override for the no-project-store fallback. */
  private readonly catalogUrlOverride?: string;
  /** Injected index SQLite path override (tests); else the default index.db. */
  private readonly indexPathOverride?: string;

  constructor(opts: AssetManagerOptions) {
    this.projectDir = resolve(opts.projectDir);
    this.driverTarget = opts.driverTarget ?? ("claude-code" as DriverTarget);
    this.global = opts.global ?? false;
    this.backendsOverride = opts.backends;
    this.injectedStoreFetcher = opts.storeFetcher;
    this.injectedAssetFetcher = opts.assetFetcher;
    this.catalogUrlOverride = opts.catalogUrl;
    this.indexPathOverride = opts.indexPath;
  }

  /**
   * Root for this manager's **state** — config SSOT, lock, cache, history.
   * `~/.skaile` in global mode (the cross-backend SSOT), else the project dir.
   * Deploy dirs are unaffected; they come from `DRIVER_TARGETS[target]`.
   */
  private get stateRoot(): string {
    return this.global ? globalSkaileDir() : this.projectDir;
  }

  /**
   * The backends a global deploy fans out to: the explicit `backends` override
   * (a `--backend` filter), else `resolveGlobalBackends()`. In project mode this
   * is exactly `[driverTarget]`, so the shared fan-out loop degenerates to the
   * single-target path.
   */
  private targetBackends(): DriverTarget[] {
    if (!this.global) return [this.driverTarget];
    if (this.backendsOverride && this.backendsOverride.length > 0) return this.backendsOverride;
    return resolveGlobalBackends();
  }

  private get storeCacheDir(): string {
    if (this.global) return join(globalCacheRoot(), "store");
    return migrateLegacyDir(
      join(this.projectDir, ".skaile", "cache", "store"),
      join(this.projectDir, ".skaile", "store-cache"),
    );
  }

  private get reposDir(): string {
    if (this.global) return join(globalCacheRoot(), "sources");
    return migrateLegacyDir(
      join(this.projectDir, ".skaile", "cache", "sources"),
      join(this.projectDir, ".skaile", "repos"),
    );
  }

  /**
   * Project-scoped cache dirs rooted at `.skaile/cache/` (`store`, `sources`)
   * relative to `projectDir`. Reading is a side effect: it triggers lazy
   * migration of the legacy `.skaile/{store-cache,repos}` dirs.
   */
  get projectCacheDirs(): { store: string; sources: string } {
    return { store: this.storeCacheDir, sources: this.reposDir };
  }

  private get lockPath(): string {
    return join(this.stateRoot, "skaile.lock.yaml");
  }

  private get patchDir(): string {
    return join(this.projectDir, ".skaile", "patches");
  }

  /** Deploy options for one backend. In global mode, `driverTarget` is the
   * fan-out target (not the constructor's `driverTarget`, which is project-only). */
  private deployOptsFor(target: DriverTarget, link?: boolean): DeployOptions {
    return { driverTarget: target, global: this.global, cwd: this.projectDir, link: link === true };
  }

  /**
   * Render the project's declared MCP servers into the driver target's own
   * native MCP config (`<project>/.mcp.json` for `claude-code`), so the coding
   * agent sees the same servers skaile's agent loop wires up.
   *
   * Project scope only: a `--global` install has no project-scope config to
   * write, and the coding agent's user scope is a large user-state file this
   * must not touch. Never fails an install — a render error is warned and
   * swallowed.
   */
  private async renderDriverMcpConfig(): Promise<void> {
    if (this.global) return;
    try {
      await renderMcpServerConfig(this.projectDir, this.driverTarget);
    } catch (err) {
      console.warn(
        `skaile: could not render MCP config for ${this.driverTarget}: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }
  }

  private loadConfig(): {
    repositories: Record<string, SourceDeclaration>;
    sources: SourceEntry[];
    stores: StoreEntry[];
    dependencies: string[];
    overrides: OverrideEntry[];
    patches: Record<string, string>;
    config: SkWorkspaceConfig;
  } {
    // resolveSkWorkspaceConfig runs the canonical normalizer and throws on
    // legacy keys (`repositories:` / `ai_resources:`) — propagate to the caller.
    // Global mode reads ONLY the user-scope SSOT (`~/.skaile/skaile.yaml`) so a
    // project's deps never leak into a global fan-out (and vice versa).
    const config = this.global
      ? resolveGlobalSkWorkspaceConfig()
      : resolveSkWorkspaceConfig(this.projectDir);

    const sources = config.sources ?? [];
    const stores = config.stores ?? [];
    const dependencies = config.dependencies ?? [];
    const overrides = config.overrides ?? [];
    const patches = config.patches ?? {};

    // Source clones live in the global clone cache (cache/sources); the slug is
    // the cache key for ensureRepo. getGlobalCacheDir() migrates legacy roots.
    const sourcesDir = getGlobalCacheDir();
    const repositories: Record<string, SourceDeclaration> = {};
    for (const s of sources) {
      const slug = sourceCacheKey(s.url);
      if (repositories[slug]) continue;
      repositories[slug] = { path: join(sourcesDir, slug), url: s.url };
    }

    // Implicit built-in source: the first-party assets shipped inside the
    // package (`dist/factory-assets/`). Always available, zero config — keyed by
    // its declared publisher so refs (`kind:@<publisher>/name`) and `info`
    // resolve cleanly. Skipped when the user already declares the same source
    // explicitly (avoids duplicate catalog entries) or when no bundle is present.
    const factory = resolveFactorySource();
    const factoryUrl = normalizeRepoUrl(FACTORY_SOURCE_URL);
    const alreadyDeclared = Object.values(repositories).some(
      (d) => d.url !== undefined && normalizeRepoUrl(d.url) === factoryUrl,
    );
    if (factory && !alreadyDeclared && !repositories[factory.publisher]) {
      repositories[factory.publisher] = {
        path: factory.dir,
        url: FACTORY_SOURCE_URL,
        factory: true,
      };
    }

    return { repositories, sources, stores, dependencies, overrides, patches, config };
  }

  /** Build provenance-index clones from the resolved source declarations. */
  private buildClones(repositories: Record<string, SourceDeclaration>): SourceClone[] {
    const clones: SourceClone[] = [];
    for (const [slug, decl] of Object.entries(repositories)) {
      const localPath = this.resolveRepoDir(decl, slug);
      if (!localPath || !existsSync(localPath)) continue;
      const commit = getRepoCommit(localPath) ?? "0".repeat(40);
      clones.push({
        localPath,
        sourceUrl: decl.url ?? localPath,
        commit,
        tag: decl.branch,
        // A curated store manifest (`source manifest init` → the flat
        // `~/.skaile/store/manifests/<slug>.yaml`) is the authoritative inventory
        // for a foreign source. Point the walker at it so `add`/resolve honor the
        // curated publisher + asset set — the same overlay `source add` indexed.
        overlayManifestPath: decl.url ? storeOverlayPathForUrl(decl.url) : undefined,
      });
    }
    return clones;
  }

  /**
   * Bridge a resolved {@link ProvenanceCandidate} to a {@link CatalogEntry} for
   * the deploy pipeline. `source` is the absolute path to the asset's primary
   * manifest (or its directory) inside the clone; `publisher` is set to the
   * clone slug so `deployAll`'s repositories lookup resolves local-vs-remote.
   */
  private candidateToCatalogEntry(
    c: ProvenanceCandidate,
    repositories: Record<string, SourceDeclaration>,
  ): CatalogEntry | null {
    // Find the clone slug whose declaration URL matches the candidate's source.
    let slug: string | undefined;
    let cloneDir: string | undefined;
    for (const [s, decl] of Object.entries(repositories)) {
      if (decl.url === c.sourceUrl) {
        slug = s;
        cloneDir = this.resolveRepoDir(decl, s) ?? undefined;
        break;
      }
    }
    if (!cloneDir) {
      // Unknown source — store assets whose bytes get staged (deployable kinds
      // plus mcp-server) are routed via `stagedCandidateToEntry` before reaching
      // here, so this branch now only hits the never-staged kinds (bundle and the
      // mixin kinds), which deployAll skips. Fall back to first clone.
      const first = Object.entries(repositories)[0];
      if (first) {
        slug = first[0];
        cloneDir = this.resolveRepoDir(first[1], first[0]) ?? undefined;
      }
    }
    if (!cloneDir || !slug) return null;

    // Resolve a concrete manifest file (suffix-aware for prompt/flow), falling
    // back to the first file — never the clone dir, which deploy can't copy as a
    // single asset (cpSync without recursive throws on a directory).
    const mdRel =
      matchManifestPath(
        c.kind,
        c.files.map((f) => f.path),
      ) ?? c.files[0]?.path;
    const source = mdRel ? join(cloneDir, mdRel) : cloneDir;

    const entry: CatalogEntry = {
      name: c.name,
      kind: c.kind as CatalogEntry["kind"],
      description: c.description ?? "",
      source,
      publisher: slug,
      version: c.version,
      requires: [],
      // Manifest-declared deps are authoritative for the candidate's deps.
      dependencies: c.deps ?? [],
      metadata: c.metadata,
    };
    if (c.category != null) entry.category = c.category;
    if (c.keywords && c.keywords.length > 0) entry.keywords = c.keywords;
    if (c.license != null) entry.license = c.license;
    if (c.homepage != null) entry.homepage = c.homepage;
    return entry;
  }

  /**
   * Resolve refs the source/store pass could not against the user's registered
   * **libraries** (local authoring places). Each library dir is scanned on disk
   * under its declared publisher (its root `skaile.manifest.yaml`, falling back
   * to the kebab-cased library name); a matching asset becomes a deployable
   * {@link CatalogEntry} whose `source` points at the on-disk manifest. This is
   * the consume side of `library create` — a freshly authored asset is
   * deployable with `skaile add <kind>:@<publisher>/<name>` without publishing.
   *
   * Pure local read (no index, no network) and mirrors how `findSkills`
   * discovers library content. Library assets are deliberately NOT written to
   * the lock (a local authoring place is not a reproducible source); they are
   * re-discovered on every `add`/`install`, so reconcile never treats them as
   * stale.
   *
   * @returns the matched deploy entries plus the set of `missing` refs covered.
   */
  private async resolveLibraryEntries(missing: string[]): Promise<{
    entries: CatalogEntry[];
    resolved: Set<string>;
  }> {
    const resolved = new Set<string>();
    if (missing.length === 0) return { entries: [], resolved };

    let librariesRoot: string;
    try {
      librariesRoot = resolveLibraryDir();
    } catch {
      return { entries: [], resolved };
    }
    if (!existsSync(librariesRoot)) return { entries: [], resolved };

    // Index every library asset by its canonical `kind:@publisher/name` key. On
    // a cross-library collision keep the highest version; on an (unlikely) equal
    // version the first library scanned keeps it (readdirSync order) — the copies
    // are equivalent enough that the pick is immaterial.
    const byKey = new Map<string, CatalogEntry>();
    for (const dirent of readdirSync(librariesRoot, { withFileTypes: true })) {
      if (!dirent.isDirectory()) continue;
      const libPath = join(librariesRoot, dirent.name);
      const publisher = readDeclaredPublisher(libPath) ?? slugifyAssetName(dirent.name);
      for (const entry of await scanDirectory(libPath, publisher)) {
        const key = `${entry.kind}:@${entry.publisher}/${entry.name}`;
        const prev = byKey.get(key);
        if (
          !prev ||
          semverGt(coerceVersion(entry.version ?? ""), coerceVersion(prev.version ?? ""))
        ) {
          byKey.set(key, entry);
        }
      }
    }
    if (byKey.size === 0) return { entries: [], resolved };

    const entries: CatalogEntry[] = [];
    for (const ref of missing) {
      let parsed: ReturnType<typeof parseAssetRef>;
      try {
        parsed = parseAssetRef(ref);
      } catch {
        continue;
      }
      if (!parsed.publisher) continue;
      const entry = byKey.get(`${parsed.kind}:@${parsed.publisher}/${parsed.name}`);
      if (!entry) continue;
      // Honour an explicit version pin (SemVer range, exact, or SHA); an unpinned
      // ref takes the on-disk asset. Same matcher `resolveAll` uses for sources.
      if (parsed.pin && !matchPin(parsed.pin, entry.version ?? "")) continue;
      entries.push(entry);
      resolved.add(ref);
    }
    return { entries, resolved };
  }

  // ── Store fetch-then-deploy ────────────────────────────────────────────

  /** A candidate's stable identity key (`<publisher>/<kind>:<name>@<version>`). */
  private candidateKey(c: ProvenanceCandidate): string {
    return `${c.publisher}/${c.kind}:${c.name}@${c.version}`;
  }

  /**
   * Resolve `deps`, falling back to the global `catalog.url` store when the
   * project declares no store and the first pass leaves something missing.
   *
   * The first pass uses the project stores only, so locally-resolvable refs
   * never trigger a network probe. The fallback retry is best-effort: any error
   * (offline, store down) keeps the first pass's result rather than regressing.
   */
  private async resolveWithStores(
    deps: string[],
    opts: {
      provenanceIndex: Awaited<ReturnType<typeof buildProvenanceIndex>>;
      overrides: OverrideEntry[];
      stores: StoreEntry[];
    },
  ): Promise<ResolveResult> {
    const first = await resolveAll(deps, {
      provenanceIndex: opts.provenanceIndex,
      overrides: opts.overrides,
      stores: opts.stores,
      storeFetcher: this.storeFetcherFor(opts.stores),
      projectDir: this.projectDir,
    });
    if (first.missing.length === 0) return first;

    const fallback = await this.globalStoreFallback(opts.stores);
    if (fallback.length === 0) return first;

    const stores = [...opts.stores, ...fallback];
    try {
      const retry = await resolveAll(deps, {
        provenanceIndex: opts.provenanceIndex,
        overrides: opts.overrides,
        stores,
        storeFetcher: this.storeFetcherFor(stores),
        projectDir: this.projectDir,
      });
      // Monotone: same provenanceIndex + overrides, only more stores added —
      // resolution can gain deps but never lose one, so fewer-missing wins.
      return retry.missing.length < first.missing.length ? retry : first;
    } catch {
      return first;
    }
  }

  /** The catalog fetcher to use: an injected one, else built from `stores`. */
  private storeFetcherFor(stores: StoreEntry[]): StoreFetcher | undefined {
    return this.injectedStoreFetcher ?? this.buildStoreFetcher(stores);
  }

  /**
   * The global `catalog.url` as a single-store fallback, or `[]` when no
   * project store should be synthesized — `local` sentinel, already-declared,
   * or no URL. Honours an explicit `catalogUrl` injection (`""` disables it).
   */
  private async globalStoreFallback(projectStores: StoreEntry[]): Promise<StoreEntry[]> {
    let url = this.catalogUrlOverride;
    if (url === undefined) {
      try {
        const { resolveConfig } = await import("@skaile/workspaces/library");
        url = resolveConfig({ projectDir: this.projectDir }).catalog.url;
      } catch {
        return [];
      }
    }
    if (!url || url.trim().toLowerCase() === "local") return [];
    if (projectStores.some((s) => normalizeRepoUrl(s.url) === normalizeRepoUrl(url!))) return [];
    return [{ url }];
  }

  /**
   * Build deployable catalog entries from resolved candidates. Store-resolved
   * candidates (no local clone bytes) are fetched + SHA-verified into the
   * project store-cache first, then their entry points at the staged bytes so
   * the existing copy-deploy path works. Source-backed candidates keep the
   * clone-relative entry.
   */
  private async buildCatalogEntries(
    resolved: ProvenanceCandidate[],
    repositories: Record<string, SourceDeclaration>,
  ): Promise<CatalogEntry[]> {
    const staged = await this.stageStoreAssets(resolved, repositories);
    const entries: CatalogEntry[] = [];
    for (const c of resolved) {
      const stagedPath = staged.get(this.candidateKey(c));
      const entry = stagedPath
        ? this.stagedCandidateToEntry(c, stagedPath)
        : this.candidateToCatalogEntry(c, repositories);
      if (entry) entries.push(entry);
    }
    return entries;
  }

  /**
   * Fetch + SHA-verify the bytes of every store-resolved candidate that needs
   * bytes on disk into `.skaile/cache/store/`, reusing the same verified-fetch
   * path the pointer-only `library.install` uses (`installFromManifest` flat
   * mode). That is the deployable kinds plus `mcp-server` — see the skip guard.
   *
   * @returns Map of candidate key → staged primary-manifest path.
   */
  private async stageStoreAssets(
    resolved: ProvenanceCandidate[],
    repositories: Record<string, SourceDeclaration>,
  ): Promise<Map<string, string>> {
    const staged = new Map<string, string>();
    let installFromManifest:
      | typeof import("@skaile/workspaces/library").installFromManifest
      | undefined;

    for (const c of resolved) {
      // Non-deployable ≠ not fetched: `mcp-server` is never copied to the driver
      // target dir, but `loadMcpServerDeclarations` reads its staged `MCP.md` at
      // session start — skipping the fetch made a store-sourced MCP a silent
      // no-op (#462). The other mixin kinds (persona/ruleset/knowledge) stay
      // skipped on purpose: their resolver (`runner/src/mixin-resolver.ts`) only
      // reads a clone-derived `RepoMap`, so staged bytes would be pure waste.
      if (NON_DEPLOYABLE_KINDS.has(c.kind) && c.kind !== "mcp-server") continue;
      if (!c.files || c.files.length === 0 || !c.sourceUrl || !c.commit) continue;
      if (this.candidateHasLocalBytes(c, repositories)) continue;

      if (!installFromManifest) {
        ({ installFromManifest } = await import("@skaile/workspaces/library"));
      }
      const targetDir = join(this.storeCacheDir, c.publisher, c.kind, c.name, c.version);
      // Per-file paths in c.files are attacker-controlled but validated by
      // installFromManifest before any fetch/write (rejects abs / `..`); this
      // outer guard only asserts the staging dir stays inside the cache root.
      const cacheRoot = resolve(this.storeCacheDir);
      if (!resolve(targetDir).startsWith(cacheRoot + sep)) {
        throw new Error(`unsafe store-cache path for ${this.candidateKey(c)}`);
      }
      const manifest: InstallManifest = {
        ref: `${c.kind}:@${c.publisher}/${c.name}#${c.version}`,
        source: { url: c.sourceUrl, commitSha: c.commit },
        files: c.files,
        sha256: c.sha256,
        kind: c.kind,
        // tier = catalog storage hint (S3/CDN); flat-mode verified fetch pulls
        // from source.url@commit and ignores it, so "" is inert.
        tier: "",
      };
      const { installPath } = await installFromManifest({
        manifest,
        libraryRoot: this.storeCacheDir,
        targetDir,
        fetcher: this.injectedAssetFetcher,
      });
      staged.set(this.candidateKey(c), join(installPath, this.stagedManifestRel(c)));
    }
    return staged;
  }

  /** Repo-relative path of a candidate's primary manifest (un-stripped). */
  private primaryManifestRel(c: ProvenanceCandidate): string | undefined {
    const paths = (c.files ?? []).map((f) => f.path);
    // Last resort: a kind with no manifest convention falls back to the first
    // file so deploy still has a concrete source rather than crashing.
    return matchManifestPath(c.kind, paths) ?? paths[0];
  }

  /**
   * The primary-manifest path relative to a staged asset dir. Takes the kind's
   * manifest (e.g. `SKILL.md`), then strips the same common-dir prefix
   * `installFromManifest` flat mode removed on write.
   */
  private stagedManifestRel(c: ProvenanceCandidate): string {
    const paths = (c.files ?? []).map((f) => f.path);
    const mdRel = this.primaryManifestRel(c) ?? "";
    const root = commonDirPrefix(paths);
    return root && mdRel.startsWith(`${root}/`) ? mdRel.slice(root.length + 1) : mdRel;
  }

  /**
   * `true` when a candidate's primary manifest is present on a local source
   * clone — its `sourceUrl` matches a registered source AND the manifest file
   * exists on disk at the expected path. A URL match alone is not enough: a
   * store can pin a different commit (or a subtree not bundled) of a repo that
   * is also a registered source, in which case the bytes must be fetched from
   * the store rather than read from the clone. (Only the primary manifest is
   * probed — a partially-cloned source is deployed as-is, matching the existing
   * clone-deploy path.)
   */
  private candidateHasLocalBytes(
    c: ProvenanceCandidate,
    repositories: Record<string, SourceDeclaration>,
  ): boolean {
    if (!c.sourceUrl) return false;
    const rel = this.primaryManifestRel(c);
    for (const [slug, decl] of Object.entries(repositories)) {
      if (decl.url === undefined || decl.url !== c.sourceUrl) continue;
      const dir = this.resolveRepoDir(decl, slug);
      if (!dir) continue;
      if (existsSync(rel ? join(dir, rel) : dir)) return true;
    }
    return false;
  }

  /** Build a copy-deploy catalog entry pointing at staged store bytes. */
  private stagedCandidateToEntry(c: ProvenanceCandidate, stagedManifestPath: string): CatalogEntry {
    const entry: CatalogEntry = {
      name: c.name,
      kind: c.kind as CatalogEntry["kind"],
      description: c.description ?? "",
      source: stagedManifestPath,
      // Real publisher (never a clone slug) → deployAsset copies rather than
      // symlinking the ephemeral store-cache dir.
      publisher: c.publisher,
      version: c.version,
      requires: [],
      dependencies: c.deps ?? [],
      metadata: c.metadata,
    };
    if (c.category != null) entry.category = c.category;
    if (c.keywords && c.keywords.length > 0) entry.keywords = c.keywords;
    if (c.license != null) entry.license = c.license;
    if (c.homepage != null) entry.homepage = c.homepage;
    return entry;
  }

  // ── Install / resolve ──────────────────────────────────────────────────

  /**
   * Install all dependencies declared in `skaile.yaml`.
   *
   * Steps: clone/pull repositories → resolve transitive dependencies → reconcile
   * (remove stale assets) → deploy new assets → write `skaile.lock.yaml` → record history.
   *
   * @param opts - Optional installation options.
   * @param opts.locked - When `true`, restore the exact versions recorded in the existing
   *   lock file instead of resolving from source. Throws if no lock file is found.
   * @param opts.link - When `true`, symlink local-path assets instead of copying them
   *   (the author dev-loop). Machine-local / not for committing; a no-op for
   *   store/factory sources. Defaults to copy.
   * @returns {@link InstallResult} summarising what was deployed, what was missing, and any collisions.
   */
  async install(opts?: { locked?: boolean; link?: boolean }): Promise<InstallResult> {
    const { repositories, sources, stores, dependencies, overrides, patches } = this.loadConfig();

    if (opts?.locked) {
      return this.installLocked(repositories, patches);
    }

    // 1. Ensure all source clones are present/up to date.
    const cloneError = this.ensureSourceClones(repositories, sources);
    if (cloneError) return cloneError;

    // 2. Build the provenance index from the clones and resolve.
    const provenanceIndex = await buildProvenanceIndex(this.buildClones(repositories), {
      projectDir: this.projectDir,
    });
    const { resolved, missing, overridesApplied } = await this.resolveWithStores(dependencies, {
      provenanceIndex,
      overrides,
      stores,
    });

    // Fallback: deps the source/store pass missed may be local library assets.
    const lib = await this.resolveLibraryEntries(missing);

    // 3. Reconcile: remove stale assets (in old lock but not in new resolved set).
    const removed = this.reconcileRemovedAssets(resolved);

    // 4. Map candidates to catalog entries (fetching store bytes) and deploy.
    //    Fan out to every target backend (one in project mode; the resolved
    //    global set in `--global` mode) — same lock, one staged byte set.
    const entries = [...(await this.buildCatalogEntries(resolved, repositories)), ...lib.entries];
    const deployed = [
      ...new Set(
        this.targetBackends().flatMap((t) =>
          deployAll(entries, repositories, patches, this.deployOptsFor(t, opts?.link)),
        ),
      ),
    ];

    // 4b. Render declared MCP servers into the coding agent's own MCP config.
    await this.renderDriverMcpConfig();

    // 5. Write the v2 lock file (authoritative full rewrite). Skip only when
    //    there is nothing lockable AND no lock exists yet — a library-only
    //    project should not get a vacuous `skaile.lock.yaml`. When a lock DOES
    //    exist, still write (an empty rewrite clears entries for now-removed
    //    source/store deps — library assets are never locked).
    const lockWritten = resolved.length > 0 || existsSync(this.lockPath);
    if (lockWritten) {
      writeLock(this.lockPath, buildLockFile(resolved, overridesApplied));
    }

    // 6. Record history.
    this.recordInstallHistory(deployed, removed);

    // 7. Install npm peer deps for any mount/connector declared in skaile.yaml.
    const { npmInstalled, npmFailed } = await this.installRuntimeAssetDeps();

    const stillMissing = missing.filter((m) => !lib.resolved.has(m));
    return { deployed, removed, missing: stillMissing, lockWritten, npmInstalled, npmFailed };
  }

  /**
   * Ensure all source clones are present/up to date. Returns an early
   * {@link InstallResult} describing the failed source on the first error, or
   * `null` when every clone succeeds.
   */
  private ensureSourceClones(
    repositories: Record<string, SourceDeclaration>,
    sources: SourceEntry[],
  ): InstallResult | null {
    const pinBySlug = pinsBySlug(sources);
    for (const [name, decl] of Object.entries(repositories)) {
      try {
        ensureRepo(decl, name, this.reposDir, {
          projectDir: this.projectDir,
          pin: pinBySlug.get(name),
        });
      } catch (err) {
        return {
          deployed: [],
          removed: [],
          missing: [`source:${name} (${err instanceof Error ? err.message : String(err)})`],
          lockWritten: false,
        };
      }
    }
    return null;
  }

  /**
   * Best-effort twin of {@link ensureSourceClones} for the read-mostly entry
   * points (`add` / `lock`): same declarations and the same pins — a pinned
   * source must never be pulled off its pin — but a clone failure is swallowed
   * so resolution still runs against whatever is on disk.
   */
  private ensureSourceClonesQuiet(
    repositories: Record<string, SourceDeclaration>,
    sources: SourceEntry[],
  ): void {
    const pinBySlug = pinsBySlug(sources);
    for (const [name, decl] of Object.entries(repositories)) {
      try {
        ensureRepo(decl, name, this.reposDir, {
          projectDir: this.projectDir,
          pin: pinBySlug.get(name),
        });
      } catch {}
    }
  }

  /**
   * Remove stale assets — those recorded in the old lock but absent from the
   * newly resolved set. Returns the list of removed refs.
   */
  private reconcileRemovedAssets(resolved: ProvenanceCandidate[]): string[] {
    const oldLock = this.tryReadLock();
    // Must match the canonical lock-key format from buildLockFile (core/lock.ts):
    // `<kind>:@<publisher>/<name>#<version>`. A different shape here made every
    // locked asset miss the set → wrongly "removed" (undeployed) then redeployed.
    const resolvedRefs = new Set(
      resolved.map((c) => `${c.kind}:@${c.publisher}/${c.name}#${c.version}`),
    );
    const removed: string[] = [];
    if (oldLock) {
      for (const ref of Object.keys(oldLock.assets)) {
        if (!resolvedRefs.has(ref)) {
          const { kind, name } = parseCanonicalLockKey(ref);
          if (kind && name) {
            for (const t of this.targetBackends()) removeAsset(kind, name, this.deployOptsFor(t));
            removed.push(ref);
          } else {
            // Surface the skip — an unparseable lock key means a stale asset can't
            // be reconciled away and would silently survive a remove-from-yaml.
            console.warn(`skaile: cannot reconcile unparseable lock key "${ref}" — left in place.`);
          }
        }
      }
    }
    return removed;
  }

  /** Append `add`/`remove` history entries for a completed install reconcile. */
  private recordInstallHistory(deployed: string[], removed: string[]): void {
    for (const ref of deployed) {
      appendHistory(this.projectDir, {
        ref,
        action: "add",
        timestamp: new Date().toISOString(),
        context: this.global ? "global" : "project",
      });
    }
    for (const ref of removed) {
      appendHistory(this.projectDir, {
        ref,
        action: "remove",
        timestamp: new Date().toISOString(),
        context: this.global ? "global" : "project",
      });
    }
  }

  /** Read the lock, returning null on a legacy v1 lock instead of throwing. */
  private tryReadLock(): LockFile | null {
    try {
      return readLock(this.lockPath);
    } catch {
      return null;
    }
  }

  /** Build a store fetcher from the configured stores, or undefined when none. */
  private buildStoreFetcher(stores: StoreEntry[]): StoreFetcher | undefined {
    if (stores.length === 0) return undefined;
    return {
      getInstallManifest: async (storeUrl, ref) => {
        const { RemoteCatalogSource, CatalogHttpError } = await import(
          "@skaile/workspaces/library"
        );
        const src = new RemoteCatalogSource({ baseUrl: storeUrl });
        try {
          const m = await src.getInstallManifest(ref);
          return {
            sourceUrl: m.source.url,
            commit: m.source.commitSha,
            sha256: m.sha256,
            files: m.files,
          };
        } catch (err) {
          if (err instanceof CatalogHttpError && err.status === 404) return null;
          throw err;
        }
      },
      getCanonicalDigest: async (storeUrl, ref) => {
        const { RemoteCatalogSource } = await import("@skaile/workspaces/library");
        const src = new RemoteCatalogSource({ baseUrl: storeUrl });
        return src.getCanonicalDigest(ref);
      },
      listVersions: async (storeUrl, ref) => {
        const { RemoteCatalogSource } = await import("@skaile/workspaces/library");
        const src = new RemoteCatalogSource({ baseUrl: storeUrl });
        try {
          return await src.listVersions(ref);
        } catch (err) {
          // Version discovery is a best-effort hint; a store/transport failure must
          // not fail the whole add/install. Warn, though — without it the ref just
          // lands in `missing` and a store outage reads as "Asset not found".
          console.warn(`skaile: store ${storeUrl} version lookup for ${ref} failed — ${err}`);
          return [];
        }
      },
    };
  }

  /**
   * Resolve runtime assets and install any missing npm peer deps.
   *
   * - Required deps: installed via `bun add --optional`. Failure is recorded
   *   in `npmFailed` but does not throw — install() proceeds so users get a
   *   useful error message rather than a hung command.
   * - Optional deps: not installed automatically (would force every consumer
   *   to pull every connector's deps). Future: add a flag to opt in.
   */
  private async installRuntimeAssetDeps(): Promise<{
    npmInstalled: string[];
    npmFailed: string[];
  }> {
    const npmInstalled: string[] = [];
    const npmFailed: string[] = [];
    let runtimeAssets: Awaited<ReturnType<typeof resolveRuntimeAssets>> | null = null;
    try {
      runtimeAssets = await resolveRuntimeAssets(this.projectDir);
    } catch {
      // Non-fatal — projects with no skaile.yaml or unresolvable factory-assets
      // simply have no runtime deps to install.
      return { npmInstalled, npmFailed };
    }

    const required = runtimeAssets.requiredNpmDeps.required;
    if (required.length === 0) return { npmInstalled, npmFailed };

    const { findMissingPackages, installNpmPackages } = await import(
      "@skaile/workspaces/connectors"
    );
    const missing = findMissingPackages(required, this.projectDir);
    if (missing.length === 0) return { npmInstalled, npmFailed };

    const result = await installNpmPackages(missing, this.projectDir);
    if (result.success) {
      npmInstalled.push(...missing);
    } else {
      npmFailed.push(...missing);
    }
    return { npmInstalled, npmFailed };
  }

  private async installLocked(
    repositories: Record<string, SourceDeclaration>,
    patches: Record<string, string>,
  ): Promise<InstallResult> {
    const lock = this.tryReadLock();
    if (!lock) {
      throw new Error("No lock file found. Run `skaile install` first.");
    }

    // Ensure every contributing source clone exists at its pinned commit. Sources
    // are keyed by URL slug; the lock's `sources[]` carries the URL + commit.
    const declBySlug = new Map(Object.entries(repositories));

    // The factory source shares FACTORY_SOURCE_URL with the real ai-assets repo but ships
    // its bytes in-package (never fetched); resolve it to its bundled `path` + skip the fetch,
    // else verifyLock checks the ai-assets clone and false-reports every factory asset missing.
    const factoryUrl = normalizeRepoUrl(FACTORY_SOURCE_URL);
    const factoryDir = Object.values(repositories).find((d) => d.factory)?.path ?? null;

    for (const src of lock.sources) {
      if (factoryDir && normalizeRepoUrl(src.url) === factoryUrl) continue;
      const slug = sourceCacheKey(src.url);
      const decl = declBySlug.get(slug) ?? { url: src.url };
      ensureRepo(decl, slug, this.reposDir, { projectDir: this.projectDir, pin: src.commit });
    }

    // Resolve a source URL to its on-disk clone for verification.
    const resolveCloneDir = (sourceUrl: string): string | null => {
      if (factoryDir && normalizeRepoUrl(sourceUrl) === factoryUrl) return factoryDir;
      const slug = sourceCacheKey(sourceUrl);
      const decl = declBySlug.get(slug) ?? { url: sourceUrl };
      return this.resolveRepoDir(decl, slug);
    };

    // Verify integrity against the v2 lock.
    const verification = verifyLock(lock, resolveCloneDir);
    if (!verification.ok) {
      const issues = [
        ...verification.drifted.map((r) => `drifted: ${r}`),
        ...verification.missing.map((r) => `missing: ${r}`),
      ];
      throw new Error(`Lock file verification failed:\n  ${issues.join("\n  ")}`);
    }

    // Rebuild catalog entries from the lock and deploy.
    const resolved: CatalogEntry[] = [];
    for (const [ref, entry] of Object.entries(lock.assets)) {
      const { publisher, kind, name } = parseCanonicalLockKey(ref);
      if (!publisher || !kind || !name) continue;
      const slug = sourceCacheKey(entry.source.url);
      const cloneDir = resolveCloneDir(entry.source.url) ?? join(this.reposDir, slug);
      const mdRel =
        entry.files.find((f) => /\.(md|yaml)$/i.test(f.path))?.path ?? entry.files[0]?.path ?? "";
      resolved.push({
        name,
        kind: kind as CatalogEntry["kind"],
        description: "",
        source: mdRel ? join(cloneDir, mdRel) : cloneDir,
        // Keep the publisher label slash-free (repo name), not the full cache key.
        publisher: sourceShortName(entry.source.url),
        version: entry.source.commit,
        requires: [],
        dependencies: [],
      });
      // Ensure the repositories map has an entry for deployAll's local/remote check.
      if (!repositories[slug]) repositories[slug] = { url: entry.source.url, path: cloneDir };
    }

    const deployed = [
      ...new Set(
        this.targetBackends().flatMap((t) =>
          deployAll(resolved, repositories, patches, this.deployOptsFor(t)),
        ),
      ),
    ];
    await this.renderDriverMcpConfig();
    return { deployed, removed: [], missing: [], lockWritten: false };
  }

  /**
   * Add a single asset (and its transitive dependencies) to the workspace.
   *
   * Resolves `ref`, deploys the asset and all requirements, then records the ref
   * in `skaile.yaml` `dependencies` so subsequent `skaile install` runs re-deploy it.
   *
   * @param ref - Asset ref in `kind:name` or `repo/kind:name` form (e.g. `skill:my-skill`).
   * @param opts - Optional deploy options.
   * @param opts.link - When `true`, symlink local-path assets instead of copying them
   *   (the author dev-loop). Machine-local / not for committing; a no-op for
   *   store/factory sources. Defaults to copy.
   * @returns Array of asset refs that were deployed in this call.
   * @throws When the asset cannot be found in any configured repository.
   */
  async add(ref: string, opts?: { link?: boolean }): Promise<string[]> {
    const { repositories, sources, stores, overrides, patches } = this.loadConfig();
    const parsed = parseAssetRef(ref);

    this.ensureSourceClonesQuiet(repositories, sources);

    const provenanceIndex = await buildProvenanceIndex(this.buildClones(repositories), {
      projectDir: this.projectDir,
    });
    const { resolved, missing, overridesApplied } = await this.resolveWithStores([ref], {
      provenanceIndex,
      overrides,
      stores,
    });

    // Fallback: a ref the source/store pass missed may be a local library asset.
    const lib = await this.resolveLibraryEntries(missing);
    if (resolved.length === 0 && lib.entries.length === 0) {
      throw new Error(
        `Asset not found: ${ref}${missing.length ? ` (missing: ${missing.join(", ")})` : ""}`,
      );
    }

    const entries = [...(await this.buildCatalogEntries(resolved, repositories)), ...lib.entries];
    const deployed = [
      ...new Set(
        this.targetBackends().flatMap((t) =>
          deployAll(entries, repositories, patches, this.deployOptsFor(t, opts?.link)),
        ),
      ),
    ];

    // Add to skaile.yaml dependencies (canonical ref string). Global mode routes
    // this to the user-scope SSOT (`~/.skaile/skaile.yaml`).
    this.addDependencyToConfig(ref);

    // Render declared MCP servers into the coding agent's own MCP config. Runs
    // after the dependency lands in skaile.yaml — the resolver only admits
    // declared assets.
    await this.renderDriverMcpConfig();

    // Keep the lock in sync — else a later remove-from-yaml + install has no
    // entry to diff against and the asset orphans. Merge (superset-write, never
    // drops existing entries or the plugins slice), not a full rewrite. A
    // library-only hit produces no provenance candidates (library assets are
    // never locked — see `resolveLibraryEntries`), so skip the write entirely
    // rather than stamp a vacuous lock over/into the project.
    if (resolved.length > 0) {
      const fresh = buildLockFile(resolved, overridesApplied);
      const prev = this.tryReadLock();
      if (prev) {
        const sources = new Map(prev.sources.map((s) => [s.url, s]));
        for (const s of fresh.sources) sources.set(s.url, s);
        writeLock(this.lockPath, {
          ...fresh,
          assets: { ...prev.assets, ...fresh.assets },
          sources: [...sources.values()].sort((a, b) => a.url.localeCompare(b.url)),
          // buildLockFile never emits a plugins slice — preserve the reconciler's.
          plugins: prev.plugins,
        });
      } else {
        writeLock(this.lockPath, fresh);
      }
    }

    for (const d of deployed) {
      appendHistory(this.projectDir, {
        ref: d,
        action: "add",
        timestamp: new Date().toISOString(),
        context: this.global ? "global" : "project",
      });
    }

    return deployed;
  }

  /**
   * Report the body-link **soft dependencies** of the given assets that are not
   * already present in the project.
   *
   * Looks up each ref's persisted `softDeps` in the local index
   * (`~/.skaile/index.db`, populated by `skaile source sync`) and returns the
   * edges whose target is neither a declared dependency nor deployed on disk. It
   * does **not** install anything and never touches the resolver / lock /
   * reconcile path: callers surface the result as a hint, the user opts in with
   * a follow-up `skaile add`. Inspects exactly the passed `refs` — soft deps of
   * their transitively-deployed dependencies are not surfaced here.
   *
   * Opening the index runs its idempotent migrations, so this is not a pure
   * read — but it is **best-effort**: any failure (legacy/corrupt index, a
   * removed-backend guard) yields `[]`, so it can never fail a primary `add`
   * that already succeeded.
   *
   * @param refs - Asset refs to inspect (canonical, `kind:@pub/name`, or `kind:name`).
   * @returns One edge per absent soft-dep target (deduped by target). `toRef` is
   *   the parseable `skaile add` ref, omitted when the target isn't in the index.
   */
  async reportSoftDeps(
    refs: string[],
  ): Promise<Array<{ from: string; to: string; toRef?: string }>> {
    if (refs.length === 0) return [];
    return this.withIndexDefs(
      (allDefs) => this.collectSoftDeps(allDefs, refs, true),
      [] as SoftDepEdge[],
    );
  }

  /**
   * All body-link soft dependencies of the given assets — present or not.
   *
   * Unlike {@link reportSoftDeps} (which filters to *absent* targets so it can
   * nudge a follow-up `skaile add`), this returns the asset's full inferred
   * soft-dep set for display surfaces (`skaile info` / `tree` / `why`). Read-only
   * and best-effort: any index failure yields `[]`. Targets are deduped.
   *
   * @param refs - Asset refs to inspect (canonical, `kind:@pub/name`, or `kind:name`).
   * @returns One {@link SoftDepEdge} per soft-dep target. `toRef` is the parseable
   *   ref, omitted when the target isn't in the index.
   */
  async softDepsOf(refs: string[]): Promise<SoftDepEdge[]> {
    if (refs.length === 0) return [];
    return this.withIndexDefs((allDefs) => this.collectSoftDeps(allDefs, refs, false), []);
  }

  /**
   * Reverse lookup: refs (canonical ids) of cached assets whose persisted
   * `softDeps` include `targetRef`. Scans the whole index, not just present
   * assets. Read-only and best-effort (`[]` on any index failure).
   *
   * @param targetRef - Canonical soft-dep target ref (`<publisher>/<name>@<version>`).
   * @returns Def ids that declare `targetRef` as a soft dependency.
   */
  async softDepDependents(targetRef: string): Promise<string[]> {
    return this.withIndexDefs(
      (allDefs) => allDefs.filter((d) => d.softDeps?.includes(targetRef)).map((d) => d.id),
      [],
    );
  }

  /**
   * Compute which soft-dependency targets would be left **orphaned** by removing
   * `refs`: targets still present in the project that came in as one of `refs`'
   * soft dependencies and that **nothing else still needs** — neither another
   * present asset's soft-dep ({@link softDepDependents}) nor a typed `requires`.
   *
   * Pure read — never removes anything. The CLI / manage surfaces each result as
   * a prompt (default No) so the user opts in to an extra `skaile remove`. Safe to
   * call before or after the primary remove: assets being removed are excluded
   * from the "needed by others" scan, so a soft target referenced only by the
   * removed asset is still reported as an orphan.
   *
   * @param refs - Asset refs being removed (canonical, `kind:@pub/name`, or `kind:name`).
   * @returns One {@link OrphanedSoftDep} per orphaned target (deduped).
   */
  async orphanedSoftDepsAfterRemoving(refs: string[]): Promise<OrphanedSoftDep[]> {
    if (refs.length === 0) return [];
    return this.withIndexDefs((allDefs) => {
      const byId = new Map(allDefs.map((d) => [d.id, d]));
      const present = this.presentRefNames();

      // Bare names of the assets being removed — excluded from "needed by others".
      const removedNames = new Set<string>();
      for (const ref of refs) {
        for (const def of matchDefsForRef(allDefs, ref)) removedNames.add(def.name);
        try {
          removedNames.add(parseAssetRef(ref).name);
        } catch {}
      }

      // Names still needed by a *surviving* present asset (soft-dep or typed requires).
      const neededByOthers = new Set<string>();
      for (const presentRef of this.presentRefs()) {
        for (const def of matchDefsForRef(allDefs, presentRef)) {
          if (removedNames.has(def.name)) continue;
          for (const to of def.softDeps ?? []) neededByOthers.add(softDepName(to));
          // parseRequires keeps the `@pub/` scope on the name — normalize to the
          // same bare-name basis the present-check uses.
          for (const req of parseRequires(def.manifest)) neededByOthers.add(softDepName(req.name));
        }
      }

      // A present soft target is flagged even when directly declared in
      // skaile.yaml: with no install-provenance tracking we can't tell "added for
      // the removed asset" from "wanted standalone", so we report the fact (no
      // surviving referrer) and let the user decide — never auto-remove.
      const out: OrphanedSoftDep[] = [];
      const seen = new Set<string>();
      for (const ref of refs) {
        for (const def of matchDefsForRef(allDefs, ref)) {
          for (const to of def.softDeps ?? []) {
            const targetName = softDepName(to);
            if (!present.has(targetName)) continue; // not in the project — nothing to orphan
            if (removedNames.has(targetName)) continue; // also being removed
            if (neededByOthers.has(targetName)) continue; // something else still needs it
            if (seen.has(to)) continue;
            seen.add(to);
            const targetDef = byId.get(to);
            out.push({
              from: def.id,
              target: to,
              targetRef: targetDef ? depRefFromDef(targetDef) : undefined,
            });
          }
        }
      }
      return out;
    }, []);
  }

  /**
   * Open the local index, run `fn` against its cached asset defs, and close it.
   * Best-effort: no index (user never ran `source sync`), a missing file, or any
   * open/guard failure yields `fallback` — so a read surface can never throw or
   * create an empty `index.db` as a side effect.
   */
  private async withIndexDefs<T>(fn: (defs: AssetDefinition[]) => T, fallback: T): Promise<T> {
    try {
      const { LocalIndex, resolveIndexPath } = await import("@skaile/workspaces/library");
      const indexPath = this.indexPathOverride ?? resolveIndexPath();
      if (!existsSync(indexPath)) return fallback;
      const index = new LocalIndex({ indexPath });
      try {
        return fn(await index.listAssetDefs());
      } finally {
        index.close();
      }
    } catch {
      return fallback;
    }
  }

  /**
   * Build soft-dep edges for `refs` from cached defs. With `onlyAbsent`, targets
   * already present in the project are skipped (the `reportSoftDeps` nudge); else
   * the full inferred set is returned (the `softDepsOf` display). Deduped by target.
   */
  private collectSoftDeps(
    allDefs: AssetDefinition[],
    refs: string[],
    onlyAbsent: boolean,
  ): SoftDepEdge[] {
    const present = onlyAbsent ? this.presentRefNames() : null;
    const byId = new Map(allDefs.map((d) => [d.id, d]));
    const out: SoftDepEdge[] = [];
    const seen = new Set<string>();
    for (const ref of refs) {
      for (const def of matchDefsForRef(allDefs, ref)) {
        for (const to of def.softDeps ?? []) {
          if (present?.has(softDepName(to))) continue;
          if (seen.has(to)) continue;
          seen.add(to);
          const targetDef = byId.get(to);
          out.push({ from: def.id, to, toRef: targetDef ? depRefFromDef(targetDef) : undefined });
        }
      }
    }
    return out;
  }

  /**
   * Names of assets already in the project — declared deps + deployed on disk.
   * Bare name only (see {@link softDepName}): a same-named asset of a different
   * publisher/kind counts as present, so the report under-reports rather than
   * nags about something already installed.
   */
  private presentRefNames(): Set<string> {
    const names = new Set<string>();
    for (const ref of this.presentRefs()) {
      try {
        names.add(parseAssetRef(ref).name);
      } catch {}
    }
    return names;
  }

  /**
   * Present asset refs — declared `skaile.yaml` deps (canonical refs as written)
   * plus deployed assets as `kind:name`. Used by the soft-dep present-check and
   * the orphan "needed by others" scan.
   */
  private presentRefs(): string[] {
    const refs: string[] = [];
    try {
      for (const ref of resolveSkWorkspaceConfig(this.projectDir).dependencies ?? [])
        refs.push(ref);
    } catch {}
    for (const e of this.listDeployed()) refs.push(`${e.kind}:${e.name}`);
    return refs;
  }

  /**
   * Remove a deployed asset and unregister it from `skaile.yaml`.
   *
   * Deliberately does NOT re-render the driver target's MCP config: the render
   * step is async (the declaration resolver is), and making this synchronous
   * method async would break every external caller. A removed MCP server's
   * generated entry is dropped by the next {@link AssetManager.install} — see
   * {@link renderMcpServerConfig}.
   *
   * @param ref - Asset ref in `kind:name` form (e.g. `skill:my-skill`).
   * @returns `true` if the asset was found and removed, `false` if it was not deployed.
   */
  remove(ref: string): boolean {
    const parsed = parseAssetRef(ref);
    // Undeploy from every target backend; "found" if any backend had it.
    let ok = false;
    for (const t of this.targetBackends()) {
      if (removeAsset(parsed.kind, parsed.name, this.deployOptsFor(t))) ok = true;
    }
    if (ok) {
      const depRef = `${parsed.kind}:${parsed.name}`;
      this.removeDependencyFromConfig(parsed);
      this.pruneLockEntry(parsed);

      appendHistory(this.projectDir, {
        ref: depRef,
        action: "remove",
        timestamp: new Date().toISOString(),
        context: this.global ? "global" : "project",
      });
    }
    return ok;
  }

  /**
   * Drop every lock entry matching a removed asset's identity, version-agnostic so
   * a content-sha pseudo-version key is pruned too. Symmetric with {@link add}'s
   * lock-merge — stops a stale entry reporting a phantom "removed" on next install.
   */
  private pruneLockEntry(parsed: { kind: string; name: string; publisher?: string }): void {
    const lock = this.tryReadLock();
    if (!lock) return;
    let changed = false;
    for (const key of Object.keys(lock.assets)) {
      const id = parseCanonicalLockKey(key);
      if (
        id.kind === parsed.kind &&
        id.name === parsed.name &&
        (!parsed.publisher || id.publisher === parsed.publisher)
      ) {
        delete lock.assets[key];
        changed = true;
      }
    }
    if (changed) writeLock(this.lockPath, lock);
  }

  // ── Query ──────────────────────────────────────────────────────────────

  /**
   * Search the catalog across all configured repositories.
   *
   * @param query - Substring to match against entry names and descriptions (case-insensitive).
   *   Omit to return all entries.
   * @param kind - Filter by asset kind (`"skill"`, `"agent"`, `"prompt"`, `"flow"`, `"contract"`).
   *   Omit to return all kinds.
   * @returns Array of matching {@link CatalogEntry} objects from all accessible repositories.
   */
  async search(query?: string, kind?: string): Promise<CatalogEntry[]> {
    const { repositories } = this.loadConfig();
    let all: CatalogEntry[] = [];

    for (const [name, decl] of Object.entries(repositories)) {
      const repoDir = this.resolveRepoDir(decl, name);
      if (!repoDir) continue;
      all = [...all, ...(await scanRepo(repoDir, name))];
    }

    if (kind) all = all.filter((e) => e.kind === kind);
    if (query) {
      const q = query.toLowerCase();
      all = all.filter(
        (e) => e.name.toLowerCase().includes(q) || e.description.toLowerCase().includes(q),
      );
    }
    return all;
  }

  /**
   * On-disk root + config slug for every declared (and the implicit factory)
   * source that has a local clone. Companion to {@link search}: callers that
   * need to relate a scanned entry back to its source root (e.g. to stamp
   * `DOMAIN.md`-derived domain grouping) can't recover the root from the flat
   * {@link CatalogEntry} list `search` returns.
   */
  searchRepoRoots(): { repoName: string; root: string }[] {
    const { repositories } = this.loadConfig();
    const out: { repoName: string; root: string }[] = [];
    for (const [name, decl] of Object.entries(repositories)) {
      const repoDir = this.resolveRepoDir(decl, name);
      if (repoDir) out.push({ repoName: name, root: repoDir });
    }
    return out;
  }

  /**
   * Look up a single asset's full catalog entry.
   *
   * Accepts a bare `kind:name` ref (no publisher) in addition to the fully-qualified
   * `kind:@publisher/name` form — {@link listDeployed} emits publisher-less refs and
   * {@link doctor} passes them straight to this method.
   *
   * @param ref - Asset ref in `kind:name`, `kind:@publisher/name`, or `repo/kind:name` form.
   * @returns The matching {@link CatalogEntry}, or `null` when not found.
   */
  async info(ref: string): Promise<CatalogEntry | null> {
    const { repositories } = this.loadConfig();
    const parsed = parseRefTolerant(ref);
    if (!parsed) return null;

    for (const [name, decl] of Object.entries(repositories)) {
      const repoDir = this.resolveRepoDir(decl, name);
      if (!repoDir) continue;
      // Match on the entry's scanned (manifest) publisher, not the repo map key
      // (= URL slug). The two are unrelated identities under canonical schema.
      const entries = await scanRepo(repoDir, name);
      const match = entries.find(
        (e) =>
          e.kind === parsed.kind &&
          e.name === parsed.name &&
          (!parsed.publisher || e.publisher === parsed.publisher),
      );
      if (match) return match;
    }
    return null;
  }

  /**
   * List all assets currently deployed in the workspace (or global cache).
   *
   * Scans the framework deploy directories rather than the lock file, so it
   * also returns assets that were installed manually or outside of `skaile install`.
   *
   * @param kind - Filter by asset kind. Omit to return all kinds.
   * @returns Shallow {@link CatalogEntry} objects (description and version will be empty).
   */
  listDeployed(kind?: string): CatalogEntry[] {
    // Bundles are dependency groups, not deployed as files — exclude from scan
    const kinds = (
      kind ? [kind] : ["skill", "agent", "prompt", "flow", "contract"]
    ) as AiComponent[];
    const result: CatalogEntry[] = [];
    const seen = new Set<string>();
    for (const k of kinds) {
      try {
        const base = deployedBase(k, this.driverTarget, this.global, this.projectDir);
        if (!existsSync(base)) continue;
        for (const item of readdirSync(base, { withFileTypes: true })) {
          // Directories and symlinks use the directory name directly (skills, omp agents, etc.)
          // Files use the stem without extension (claude-code agents → .md, codex agents → .toml,
          // prompts → .prompt.md, flows → .flow.yaml/.flow.json)
          let name: string;
          if (item.isDirectory() || item.isSymbolicLink()) {
            name = item.name;
          } else if (item.isFile()) {
            name = item.name
              .replace(/\.prompt\.md$/, "")
              .replace(/\.flow\.(yaml|json)$/, "")
              .replace(/\.bundle\.yaml$/, "")
              .replace(/\.(md|toml|yaml|json)$/, "");
          } else {
            continue;
          }
          const key = `${k}:${name}`;
          if (seen.has(key)) continue;
          seen.add(key);
          result.push({
            name,
            kind: k as any,
            description: "",
            source: join(base, item.name),
            version: "",
            requires: [],
            dependencies: [],
          });
        }
      } catch {}
    }
    return result;
  }

  /**
   * List the **declared** top-level dependencies from `skaile.yaml`, each tagged
   * with a reconciliation {@link DeclaredState}:
   *
   * - `declared` — in `skaile.yaml`, not yet resolved into the lock.
   * - `installed` — declared + locked, and (for deployable kinds) materialized on
   *   disk. An `mcp-server` is instead checked against the declarations the runner
   *   would actually wire (store-staged or materialized `MCP.md`); the remaining
   *   non-deployable kinds ({@link NON_DEPLOYABLE_KINDS}) count as installed once
   *   locked — they are resolved at session start with no on-disk footprint here.
   * - `drift` — declared + locked but the deployed files are missing on disk.
   * - `invalid` — the `dependencies:` entry isn't a parseable canonical ref. Only
   *   surfaced in the unfiltered listing (a `kind` filter can't classify it).
   *
   * Transitive (lock-only) deps are excluded by construction — only the declared
   * set is walked, so a lock entry with no matching `dependencies:` ref never shows.
   *
   * @param kind - Filter by asset kind. Omit to return all kinds.
   * @returns One {@link DeclaredAsset} per declared dep (after the optional kind filter).
   * @throws {MissingManifestError} when the project has no `skaile.yaml`.
   * @throws {Error} with a `migrate-skaile-manifest` hint on legacy `repositories:` / `ai_resources:` keys.
   */
  async listDeclared(kind?: string): Promise<DeclaredAsset[]> {
    if (!existsSync(join(this.projectDir, SKAILE_YAML_DEFAULT))) {
      throw new MissingManifestError(this.projectDir);
    }
    // resolveSkWorkspaceConfig runs the canonical normalizer (throws on legacy
    // keys) — used directly rather than loadConfig() to avoid the latter's
    // repositories-map build + getGlobalCacheDir() migration side effects on a read.
    const dependencies = resolveSkWorkspaceConfig(this.projectDir).dependencies ?? [];
    const lock = this.tryReadLock();
    const lockKeys = lock ? Object.keys(lock.assets).map(parseCanonicalLockKey) : [];

    // Names of every MCP the runner would actually wire, in both scoped and
    // unscoped form. Built at most once per call and only when a locked
    // mcp-server is reached — the load builds a provenance index, too expensive
    // to pay for a listing that has none. A throw degrades to "nothing wired"
    // (→ drift) rather than failing the whole read-only listing.
    let wiredMcps: Set<string> | undefined;
    const wiredMcpNames = async (): Promise<Set<string>> => {
      if (wiredMcps === undefined) {
        try {
          const decls = await loadMcpServerDeclarations(this.projectDir);
          wiredMcps = new Set(decls.flatMap((d) => [d.id, unscopedName(d.id)]));
        } catch {
          wiredMcps = new Set<string>();
        }
      }
      return wiredMcps;
    };

    const out: DeclaredAsset[] = [];
    for (const ref of dependencies) {
      let parsed: ReturnType<typeof parseAssetRef>;
      try {
        parsed = parseAssetRef(ref);
      } catch {
        // An unparseable ref has no kind to match a filter against; surface it
        // as `invalid` in the unfiltered listing rather than dropping it silently.
        if (!kind) out.push({ ref, kind: "", name: ref, state: "invalid" });
        continue;
      }
      if (kind && parsed.kind !== kind) continue;

      // A declared ref need not carry a publisher; match on kind+name and, when
      // present, the publisher — the lock key is always fully canonical.
      const locked = lockKeys.some(
        (k) =>
          k.kind === parsed.kind &&
          k.name === parsed.name &&
          (!parsed.publisher || k.publisher === parsed.publisher),
      );

      let state: DeclaredState;
      if (!locked) {
        state = "declared";
      } else if (parsed.kind === "mcp-server") {
        // An mcp-server has no target-dir footprint, but it is not bytes-free
        // either: it is wired from a store-staged or materialized `MCP.md`. Only
        // "the runner would see a declaration for this name" counts as installed
        // — locked-but-invisible is real drift, fixable by `skaile install`.
        const wired = await wiredMcpNames();
        state =
          wired.has(parsed.name) || wired.has(unscopedName(parsed.name)) ? "installed" : "drift";
      } else if (NON_DEPLOYABLE_KINDS.has(parsed.kind)) {
        state = "installed";
      } else {
        const onDisk = isDeployed(
          parsed.kind as AiComponent,
          parsed.name,
          this.driverTarget,
          this.global,
          this.projectDir,
        );
        state = onDisk ? "installed" : "drift";
      }
      out.push({ ref, kind: parsed.kind, name: parsed.name, state });
    }
    return out;
  }

  /**
   * Per-backend reconciliation of the **global** declared deps
   * (`~/.skaile/skaile.yaml`) against each global backend's deploy dir. The
   * global twin of {@link listDeclared}: the same `declared` / `installed` /
   * `drift` / `invalid` vocabulary, but one row **per (ref, backend)** so an
   * asset can be `installed` for `claude-code` and `drift`/`declared` for
   * `codex` at once.
   *
   * Reads the SSOT + the global lock (`~/.skaile/skaile.lock.yaml`) — construct
   * the manager with `global: true`. Unlike `listDeclared`, a missing SSOT is
   * **not** an error: it yields an empty list (a fresh machine has no globals).
   *
   * @param opts.backend - Reconcile against this one backend only (the
   *   `--backend` filter); omit for every backend in {@link targetBackends}.
   * @param opts.kind - Filter by asset kind.
   * @returns One {@link BackendDeclaredAsset} per declared dep × backend.
   * @throws {Error} on legacy keys in `~/.skaile/skaile.yaml` (migration hint).
   */
  globalState(opts?: { backend?: DriverTarget; kind?: string }): BackendDeclaredAsset[] {
    const dependencies = resolveGlobalSkWorkspaceConfig().dependencies ?? [];
    const lock = this.tryReadLock();
    const lockKeys = lock ? Object.keys(lock.assets).map(parseCanonicalLockKey) : [];
    const backends = opts?.backend ? [opts.backend] : this.targetBackends();

    const out: BackendDeclaredAsset[] = [];
    for (const ref of dependencies) {
      let parsed: ReturnType<typeof parseAssetRef>;
      try {
        parsed = parseAssetRef(ref);
      } catch {
        if (!opts?.kind) {
          for (const backend of backends)
            out.push({ ref, kind: "", name: ref, state: "invalid", backend });
        }
        continue;
      }
      if (opts?.kind && parsed.kind !== opts.kind) continue;

      const locked = lockKeys.some(
        (k) =>
          k.kind === parsed.kind &&
          k.name === parsed.name &&
          (!parsed.publisher || k.publisher === parsed.publisher),
      );

      for (const backend of backends) {
        let state: DeclaredState;
        if (!locked) {
          state = "declared";
        } else if (NON_DEPLOYABLE_KINDS.has(parsed.kind)) {
          state = "installed";
        } else {
          const onDisk = isDeployed(parsed.kind as AiComponent, parsed.name, backend, true);
          state = onDisk ? "installed" : "drift";
        }
        out.push({ ref, kind: parsed.kind, name: parsed.name, state, backend });
      }
    }
    return out;
  }

  /**
   * Build an aggregated workspace overview with domain grouping and sync status.
   *
   * Combines deployed assets, lock file metadata, and live repository status
   * into a single structured result. Used by `skaile status`.
   *
   * @returns {@link OverviewResult} with entries grouped by domain and per-repo sync status.
   */
  overview(): OverviewResult {
    const { repositories } = this.loadConfig();
    const lock = this.tryReadLock();
    const deployed = this.listDeployed();

    const repoStatuses = this.buildRepoStatuses(repositories);
    const lockByKindName = indexLockByKindName(lock);

    // Build entries with publisher + sync info. Domain grouping is by publisher
    // now that the lock no longer records a project-local domain.
    const entries: OverviewEntry[] = [];
    for (const d of deployed) {
      const lockEntry = lockByKindName.get(`${d.kind}:${d.name}`);
      const publisher = lockEntry?.publisher ?? "";
      const version = lockEntry?.version ?? "";
      const slug = lockEntry ? sourceCacheKey(lockEntry.url) : "";
      const syncStatus = slug ? computeSyncStatus(repoStatuses.get(slug)) : "unknown";
      const domain = publisher || "unknown";
      entries.push({ kind: d.kind, name: d.name, domain, publisher, version, syncStatus });
    }

    return {
      byDomain: groupAndSortByDomain(entries),
      total: entries.length,
      repos: [...repoStatuses.values()],
    };
  }

  /** Collect live sync status for every configured repository, keyed by name. */
  private buildRepoStatuses(
    repositories: Record<string, SourceDeclaration>,
  ): Map<string, RepoStatusInfo> {
    const repoStatuses = new Map<string, RepoStatusInfo>();
    for (const [name, decl] of Object.entries(repositories)) {
      const status = checkRepoStatus(decl, name, this.reposDir);
      repoStatuses.set(name, {
        name,
        kind: status.kind,
        behind: status.behind,
        upToDate: status.upToDate,
        error: status.error,
      });
    }
    return repoStatuses;
  }

  /**
   * Build the resolved dependency tree from the lock file.
   *
   * @returns Root {@link DependencyNode} with `ref === "project"` whose children
   *   are the directly-declared dependencies, each with their own transitive subtrees.
   *   Returns a single node with `ref === "(no lock file)"` when no lock exists.
   */
  tree(): DependencyNode {
    const lock = this.tryReadLock();
    if (!lock) return { ref: "(no lock file)", children: [] };

    // v2 lock no longer records per-asset parent provenance — every locked asset
    // is shown as a direct child of the project (flat tree).
    return {
      ref: "project",
      children: Object.keys(lock.assets).map((ref) => ({ ref, children: [] })),
    };
  }

  /**
   * Explain why an asset is installed by tracing its dependency chain in the lock file.
   *
   * @param ref - Asset ref in `kind:name` form.
   * @returns Chain of refs from the asset back to `"direct (skaile.yaml)"`, or `[]` when
   *   the asset is not in the lock file.
   */
  why(ref: string): string[] {
    const lock = this.tryReadLock();
    if (!lock) return [];
    // Exact lock-key hit, else a version-agnostic match on the `kind:@pub/name`
    // prefix — the lock can pin a content-sha pseudo-version, so a bare
    // `kind:@pub/name` ref would otherwise never find its locked entry.
    const bare = (s: string) => (s.includes("#") ? s.slice(0, s.indexOf("#")) : s);
    const key = lock.assets[ref]
      ? ref
      : Object.keys(lock.assets).find((k) => bare(k) === bare(ref));
    if (!key) return []; // not in lock file
    // v2 lock no longer records a dep chain — every locked asset is reported as
    // directly declared. (Transitive provenance reconstruction is out of scope.)
    return [key, "direct (skaile.yaml)"];
  }

  /**
   * List installed assets whose source repository is behind the remote.
   *
   * Only remote repositories (not local paths) are checked for staleness.
   *
   * @returns Array of {@link OutdatedEntry} objects, one per asset in a repository
   *   that has commits not yet pulled locally.
   */
  outdated(): OutdatedEntry[] {
    const { repositories } = this.loadConfig();
    const lock = this.tryReadLock();
    if (!lock) return [];

    const result: OutdatedEntry[] = [];
    for (const src of lock.sources) {
      const slug = sourceCacheKey(src.url);
      const decl = repositories[slug];
      if (!decl || decl.path) continue; // skip unknown / local sources
      const status = checkRepoStatus(decl, slug, this.reposDir);
      if (status.behind <= 0) continue;
      for (const [ref, entry] of Object.entries(lock.assets)) {
        if (entry.source.url !== src.url) continue;
        const { publisher, kind, name, version } = parseCanonicalLockKey(ref);
        if (!kind || !name) continue;
        result.push({
          kind,
          name,
          publisher: publisher ?? sourceShortName(src.url),
          currentVersion: version ?? "",
          behind: status.behind,
        });
      }
    }
    return result;
  }

  /**
   * Produce a unified diff between the deployed asset and its catalog source.
   *
   * Useful for detecting manual edits to deployed files or verifying that a patch
   * was applied correctly.
   *
   * @param ref - Asset ref in `kind:name` form.
   * @returns Unified diff string, or `null` when there are no differences or the
   *   asset is not deployed/resolvable.
   */
  async diff(ref: string): Promise<string | null> {
    const parsed = parseAssetRef(ref);
    const entry = await this.info(ref);
    if (!entry) return null;

    const deployedPath = join(
      deployedBase(parsed.kind as AiComponent, this.driverTarget, this.global, this.projectDir),
      parsed.name,
    );
    if (!existsSync(deployedPath) || !existsSync(entry.source)) return null;

    const r = spawnSync("diff", ["-ruN", entry.source, deployedPath], { encoding: "utf8" });
    return r.status === 1 ? r.stdout : null;
  }

  // ── Lock ───────────────────────────────────────────────────────────────

  /**
   * Resolve all declared dependencies and write (or overwrite) `skaile.lock.yaml`.
   *
   * Performs repository sync and full dependency resolution without deploying assets.
   * Use this to regenerate the lock file after manually editing `skaile.yaml`.
   *
   * @returns The written {@link LockFile} data.
   */
  async lock(): Promise<LockFile> {
    const { repositories, sources, stores, dependencies, overrides } = this.loadConfig();

    this.ensureSourceClonesQuiet(repositories, sources);

    const provenanceIndex = await buildProvenanceIndex(this.buildClones(repositories), {
      projectDir: this.projectDir,
    });
    const { resolved, overridesApplied } = await this.resolveWithStores(dependencies, {
      provenanceIndex,
      overrides,
      stores,
    });
    const lockData = buildLockFile(resolved, overridesApplied);
    writeLock(this.lockPath, lockData);
    return lockData;
  }

  // ── Patch workflow ─────────────────────────────────────────────────────

  /**
   * Extract a deployed asset into a patch working directory for editing.
   *
   * Copies the deployed asset to `.skaile/patches/<kind>-<name>/` so changes
   * can be made there and then committed as a patch file via `patchCommit`.
   *
   * @param ref - Asset ref in `kind:name` form.
   * @returns Absolute path to the patch working directory.
   * @throws When the asset cannot be found in the catalog.
   */
  async patch(ref: string): Promise<string> {
    const entry = await this.info(ref);
    if (!entry) throw new Error(`Asset not found: ${ref}`);
    return extractForPatch(entry, this.patchDir);
  }

  /**
   * Generate a `.patch` file from changes made in the patch working directory.
   *
   * Diffs the patch working directory against the original catalog source and
   * writes the result to `.skaile/patches/<kind>-<name>.patch`.
   *
   * @param ref - Asset ref in `kind:name` form.
   * @returns Absolute path to the generated `.patch` file.
   * @throws When the patch working directory does not exist or no differences are found.
   */
  async patchCommit(ref: string): Promise<string> {
    const parsed = parseAssetRef(ref);
    const entry = await this.info(ref);
    if (!entry) throw new Error(`Asset not found: ${ref}`);

    const patchedDir = join(this.patchDir, `${parsed.kind}-${parsed.name}`);
    if (!existsSync(patchedDir)) {
      throw new Error(`No patch working dir found. Run \`skaile patch ${ref}\` first.`);
    }

    const originalDir = dirname(entry.source);
    const content = generatePatch(originalDir, patchedDir);
    if (!content) throw new Error("No differences found.");

    const patchFile = join(this.patchDir, `${parsed.kind}-${parsed.name}.patch`);
    savePatch(patchFile, content);
    return patchFile;
  }

  /**
   * Apply the committed patch file to the repository clone and open a contribution branch.
   *
   * Applies `.skaile/patches/<kind>-<name>.patch` to the repository clone, creates a
   * `patch/<kind>-<name>` branch, and stages a commit — ready to push via `contribPush`.
   *
   * @param ref - Asset ref in `kind:name` form.
   * @throws When the asset is not found, has no repository, or the patch file is missing.
   */
  async patchSubmit(ref: string): Promise<void> {
    const parsed = parseAssetRef(ref);
    const entry = await this.info(ref);
    if (!entry?.publisher) throw new Error(`Asset not found or no publisher: ${ref}`);

    const { repositories } = this.loadConfig();
    const decl = repositories[entry.publisher];
    if (!decl?.url) throw new Error("Can only submit patches for remote sources.");

    // Resolve through the same helper search/info use so patch + read agree on the clone dir.
    const repoDir = this.resolveRepoDir(decl, entry.publisher);
    if (!repoDir) throw new Error(`Repository clone not found for ${entry.publisher}.`);
    const branchName = `patch/${parsed.kind}-${parsed.name}`;

    // Apply patch to repo clone
    const patchFile = join(this.patchDir, `${parsed.kind}-${parsed.name}.patch`);
    if (!existsSync(patchFile)) throw new Error("No patch file. Run `skaile patch-commit` first.");

    coreApplyPatch(repoDir, patchFile);
    commitChanges(repoDir, `fix: improve ${parsed.kind} ${parsed.name}`, branchName);
  }

  /**
   * Delete the patch working directory and patch file for an asset.
   *
   * @param ref - Asset ref in `kind:name` form.
   */
  patchRemove(ref: string): void {
    const parsed = parseAssetRef(ref);
    const patchFile = join(this.patchDir, `${parsed.kind}-${parsed.name}.patch`);
    const patchedDir = join(this.patchDir, `${parsed.kind}-${parsed.name}`);
    try {
      rmSync(patchFile);
    } catch {}
    try {
      rmSync(patchedDir, { recursive: true });
    } catch {}
  }

  // ── Doctor ─────────────────────────────────────────────────────────────

  /**
   * Scan deployed assets for unsatisfied transitive dependencies.
   *
   * For each deployed asset, checks that all entries in its `requires` list are
   * also deployed. Returns a list of issues with a flag indicating whether the
   * missing dependency can be resolved from a configured repository.
   *
   * @returns Array of {@link DepIssue} objects. An empty array means the workspace is healthy.
   */
  async doctor(): Promise<DepIssue[]> {
    const { repositories } = this.loadConfig();
    const deployed = this.listDeployed();
    const issues: DepIssue[] = [];

    for (const entry of deployed) {
      const full = await this.info(`${entry.kind}:${entry.name}`);
      if (!full) continue;
      for (const req of full.requires) {
        if (
          !isDeployed(
            req.kind as AiComponent,
            req.name,
            this.driverTarget,
            this.global,
            this.projectDir,
          )
        ) {
          const inRepos =
            (await resolveAsset(
              { kind: req.kind, name: req.name },
              repositories,
              this.reposDir,
            )) !== null;
          issues.push({
            assetKind: entry.kind,
            assetName: entry.name,
            depKind: req.kind,
            depName: req.name,
            inRepos,
          });
        }
      }
    }
    return issues;
  }

  // ── Scaffold ───────────────────────────────────────────────────────────

  /**
   * Create a minimal asset scaffold directory (delegates to {@link createScaffold}).
   *
   * @param name - Asset name (also used as the directory name).
   * @param kind - Asset kind: `skill` | `agent` | `bundle` | `flow` | `prompt` | `contract`.
   * @param destDir - Parent directory to create the asset in.
   * @returns `{ ok: true, path }` on success, `{ ok: false, path: errorMessage }` if the
   *   directory already exists.
   */
  create(name: string, kind: string, destDir: string): { ok: boolean; path: string } {
    return createScaffold(name, kind, destDir);
  }

  // ── Contrib (REMOVED 2026-05-12) ───────────────────────────────────────
  //
  // The `contribChanges / contribDiff / contribCommit / contribPush /
  // contribCompare` methods were retired alongside `skaile repo contrib *`.
  // The refinement push-back workflow is being re-introduced in Phase 4 as
  // `skaile publish` + `skaile preset push` against the Library Source model.

  // ── History ────────────────────────────────────────────────────────────

  /**
   * Return recent asset action history entries, newest first.
   *
   * @param limit - Maximum number of entries to return. Defaults to `20`.
   * @returns Array of {@link HistoryEntry} objects in reverse-chronological order.
   */
  history(limit = 20): HistoryEntry[] {
    return getRecentHistory(this.projectDir, limit);
  }

  /**
   * Erase all asset action history for this workspace.
   */
  clearHistory(): void {
    clearHistory(this.projectDir);
  }

  // ── Clean ───────────────────────────────────────────────────────────────

  /**
   * Remove deployed assets tracked by the lock file and report unmanaged ones.
   *
   * With `opts.all`, also deletes `.skaile/history.yaml`, `.skaile/patches/`,
   * `.skaile/cache/sources/`, and `skaile.lock.yaml` — effectively resetting the
   * workspace to a pre-install state.
   *
   * @param opts - Optional cleanup options.
   * @param opts.all - When `true`, remove all Skaile-managed state in addition to deployed assets.
   * @returns Object with `removed` (successfully cleaned refs), `skipped` (with reasons),
   *   and `unmanaged` (deployed assets not in the lock file).
   */
  clean(opts?: { all?: boolean }): {
    removed: string[];
    skipped: Array<{ ref: string; reason: string }>;
    unmanaged: string[];
  } {
    const lock = this.tryReadLock();
    const skipped: Array<{ ref: string; reason: string }> = [];

    const removed = this.cleanLockedDeployedAssets(lock);
    const unmanaged = this.collectUnmanagedAssets(lock);

    if (opts?.all) this.removeAllManagedState();

    return { removed, skipped, unmanaged };
  }

  /**
   * Remove the deployed dir of every asset tracked by the lock; missing dirs
   * still count as removed. Returns the list of cleaned refs.
   */
  private cleanLockedDeployedAssets(lock: LockFile | null): string[] {
    const removed: string[] = [];
    if (!lock) return removed;
    for (const ref of Object.keys(lock.assets)) {
      const { kind, name } = parseCanonicalLockKey(ref);
      if (!kind || !name) continue;
      const dest = deployedDir(
        kind as AiComponent,
        name,
        this.driverTarget,
        this.global,
        this.projectDir,
      );

      try {
        const stat = lstatSync(dest);
        if (stat.isSymbolicLink()) unlinkSync(dest);
        else rmSync(dest, { recursive: true, force: true });
        removed.push(ref);
      } catch {
        // Already gone — count as removed
        removed.push(ref);
      }
    }
    return removed;
  }

  /**
   * Report deployed assets not tracked by the lock file. Deployed assets are
   * keyed by <kind>:<name>; lock canonical refs are mapped to that shape.
   */
  private collectUnmanagedAssets(lock: LockFile | null): string[] {
    const lockRefs = lock
      ? new Set(
          Object.keys(lock.assets).map((r) => {
            const p = parseCanonicalLockKey(r);
            return p.kind && p.name ? `${p.kind}:${p.name}` : r;
          }),
        )
      : new Set<string>();
    const unmanaged: string[] = [];
    const allDeployed = this.listDeployed();
    for (const d of allDeployed) {
      const ref = `${d.kind}:${d.name}`;
      if (!lockRefs.has(ref)) {
        unmanaged.push(ref);
      }
    }
    return unmanaged;
  }

  /** Delete all Skaile-managed state: history file, patches/, cache/sources/, lock. */
  private removeAllManagedState(): void {
    // Delete history file
    const historyFile = join(this.projectDir, ".skaile", "history.yaml");
    try {
      unlinkSync(historyFile);
    } catch {}

    // Remove patches dir
    try {
      rmSync(this.patchDir, { recursive: true, force: true });
    } catch {}

    // Remove repos dir
    try {
      rmSync(this.reposDir, { recursive: true, force: true });
    } catch {}

    // Remove lock file
    try {
      unlinkSync(this.lockPath);
    } catch {}
  }

  // ── Helpers ────────────────────────────────────────────────────────────

  /**
   * Add a dependency ref to the manifest `dependencies:` if not already present.
   * Project mode edits `<projectDir>/skaile.yaml`; global mode edits the
   * user-scope SSOT `~/.skaile/skaile.yaml`, **bootstrapping** the file (and its
   * parent dir) when absent so `skaile add --global` works on a fresh machine.
   */
  private addDependencyToConfig(ref: string): void {
    const configPath = join(this.stateRoot, "skaile.yaml");
    if (!existsSync(configPath)) {
      if (!this.global) return; // project mode never bootstraps a manifest
      mkdirSync(this.stateRoot, { recursive: true });
      writeFileSync(configPath, stringifyYaml({ dependencies: [ref] }, { lineWidth: 120 }));
      return;
    }
    const raw = parseYaml(readFileSync(configPath, "utf8")) ?? {};
    if (!raw.dependencies) raw.dependencies = [];
    if (!raw.dependencies.includes(ref)) {
      raw.dependencies.push(ref);
      writeFileSync(configPath, stringifyYaml(raw, { lineWidth: 120 }));
    }
  }

  /**
   * Remove a dependency ref from the manifest `dependencies:`. Targets the same
   * file as {@link addDependencyToConfig} (`~/.skaile/skaile.yaml` in global mode).
   */
  private removeDependencyFromConfig(target: {
    kind: string;
    name: string;
    publisher?: string;
  }): void {
    const configPath = join(this.stateRoot, "skaile.yaml");
    if (!existsSync(configPath)) return;
    const raw = parseYaml(readFileSync(configPath, "utf8")) ?? {};
    if (!Array.isArray(raw.dependencies)) return;
    // Match by parsed identity, not by an exact string: `add` stores deps
    // publisher-qualified (`skill:@pub/name`) while a remove caller only knows
    // `kind:name`, so an `indexOf` of the bare key never matched. When both
    // sides carry a publisher they must agree; otherwise fall back to kind+name.
    const wantPub = target.publisher?.replace(/^@/, "");
    const idx = raw.dependencies.findIndex((dep: unknown) => {
      if (typeof dep !== "string") return false;
      const p = parseRefLoose(dep);
      if (!p || p.kind !== target.kind || p.name !== target.name) return false;
      const havePub = p.publisher?.replace(/^@/, "");
      return wantPub === undefined || havePub === undefined || havePub === wantPub;
    });
    if (idx !== -1) {
      raw.dependencies.splice(idx, 1);
      writeFileSync(configPath, stringifyYaml(raw, { lineWidth: 120 }));
    }
  }

  private resolveRepoDir(decl: SourceDeclaration, name: string): string | null {
    // Check links first
    const links = readLinks(this.projectDir);
    if (links[name]) {
      return existsSync(links[name]) ? links[name] : null;
    }

    if (decl.path) {
      const resolved = resolve(this.projectDir, decl.path);
      return existsSync(resolved) ? resolved : null;
    }

    // Check project-local symlink, then global cache
    const dest = join(this.reposDir, name);
    if (existsSync(dest)) return dest;

    const globalDest = join(getGlobalCacheDir(), name);
    if (existsSync(join(globalDest, ".git"))) return globalDest;

    return null;
  }
}

// ── Agent install (renders GitAgent packages to framework-native files) ──

export { installAgent } from "./install-agent.js";

// ── Renderers ─────────────────────────────────────────────────────────────

export type { AgentRenderer, AgentRenderInput, AgentRenderResult } from "./renderers.js";
export {
  AGENT_RENDERERS,
  claudeCodeRenderer,
  codexRenderer,
  driverTargetSupportsAgents,
  ompRenderer,
} from "./renderers.js";

// ── Fragments ─────────────────────────────────────────────────────────────

export type {
  AbilityRef,
  ConnectorRef,
  ContractRef,
  FragmentContext,
  FragmentId,
} from "./fragments.js";
export { BUILT_IN_FRAGMENTS, loadPromptExtensions, resolveFragments } from "./fragments.js";
